// src/tabs/messages/DirectMessagesView.jsx — Direct A2 THIN, écran PILOTABLE.
//
// Écran Communication → Template → Direct, en 3 COLONNES :
//
//   • Colonne GAUCHE — liste : les gabarits GROUPÉS par catégorie, lus depuis la
//     base (`direct_messages_list`). Badges VOY/ADM, statut Actif/Brouillon,
//     recherche, réordonnancement DnD (lot 3B), création/suppression de messages
//     manuels (les gabarits d'origine sont insupprimables côté serveur).
//   • Colonne CENTRE — éditeur : condition d'envoi (sys verrouillé « géré par le
//     système » ; temp → heure réglable, lots H/SUIVI ADMIN), canaux, édition
//     objet + corps (lot 3A) avec bascule FR/EN sur les bilingues (lot L,
//     contrat EN tout-ou-rien), palette de variables, « Revenir au défaut ».
//   • Colonne DROITE — aperçu « ce que reçoit le voyageur », sticky.
//
// SOURCE DU CONTENU — BASCULE B1 ACTÉE (2026-07-08, prod DIRECT_TEMPLATES_SOURCE
// ='db') : les envois réels du parcours Direct lisent les lignes ÉDITÉES
// (is_edited=true) de `direct_messages` via lib/journey-content-source.js ; une
// ligne non éditée → builder codé (lib/journey-templates.js). Une édition
// enregistrée ici S'APPLIQUE donc aux envois réels ; « Revenir au défaut »
// restaure le builder. Statut/canal/heure ne gatent PAS le contenu (périmètre
// B1 contenu seul — la pause des temporels = lot B2 séparé).

import { memo, useEffect, useMemo, useRef, useState } from "react";
import {
  Search,
  Mail,
  MailPlus,
  CheckCheck,
  Signature,
  Banknote,
  CheckCircle2,
  BellRing,
  PlaneTakeoff,
  ShieldHalf,
  Folder,
  Lock,
  Zap,
  CalendarClock,
  User,
  ShieldCheck,
  Eye,
  Plus,
  Puzzle,
  GripVertical,
  ChevronDown,
  Clock,
  Flower2,
  Route,
  Radio,
  Save,
  RotateCcw,
  Trash2,
  MoreVertical,
  Filter,
  History,
  Star,
  AlertTriangle,
} from "lucide-react";
import { DS } from "../../lib/ui";
import { useIsMobile } from "../../lib/useIsMobile";
import {
  fetchDirectMessages,
  fetchDirectMessagesBuilders,
  fetchDirectConditions,
  saveDirectMessage,
  resetDirectMessage,
  createDirectMessage,
  deleteDirectMessage,
  reorderDirectMessages,
  saveDirectMessageSettings,
} from "../../lib/direct-messages-api";
import { VAR_LABELS_FR } from "../../lib/journey-var-labels";
// M12 — éditeur segmenté PARTAGÉ avec Plateformes (runs + pastilles de
// variables + picker « + Info voyageur ») : la string stockée reste la source
// de vérité ({tokens} en base), les pastilles ne sont qu'une couche
// d'affichage/édition (re-sérialisation byte-identique).
import SegmentEditor from "./segment-editor/SegmentEditor";
import VarPickerButton from "./segment-editor/VarPickerButton";
// B4 (ADR-272) — blocs conditionnels {#if …} PROPRES au Direct (menu «
// + Paragraphe conditionnel » + bandeaux d'encart), alimentés par le catalogue
// B2. Miroir du mécanisme Plateformes, conditions Direct au lieu de is_airbnb/….
import {
  DIRECT_CONDITION_BLOCK_GROUPS,
  directIfEncartMeta,
} from "./segment-editor/direct-blocks";
// B5 (ADR-272) — APERÇU INTERACTIF : le moteur client rejoue les DEUX passes du
// serveur ({#if …} puis {token}) et le panneau de scénario fournit les drapeaux.
import { renderDirectPreview } from "../../lib/direct-preview-engine";
import {
  DEFAULT_DIRECT_SCENARIO,
  buildDirectPreviewVars,
  scenarioGroupsForTexts,
} from "../../lib/direct-preview-scenario";
import DirectPreviewScenarioPanel from "./DirectPreviewScenarioPanel";
// Nom du groupe « Références & annulation » — source unique partagée avec le
// catalogue serveur (lib/direct-vars-catalog.js).
import { GROUPE_REFERENCES } from "../../lib/direct-var-groups";

// ── PERF « aperçu à la frappe » ──────────────────────────────────────────────
// MESURE (src/tabs/messages/DirectMessagesView.perf-apercu.test.jsx) : à chaque
// frappe dans l'éditeur, TOUT l'écran Direct était reconstruit — le rail de
// gauche (une trentaine de lignes), le panneau « Simuler une réservation »
// (420 lignes) et l'aperçu — alors qu'aucun des trois, sauf l'aperçu, ne dépend
// du texte en cours de saisie. Le moteur d'aperçu lui-même ne pesait presque
// rien (≈ 0,3 % du temps) : la vraie cause était le RE-RENDU, pas le rendu de
// gabarit. Trois mémoïsations ciblées, ZÉRO changement de rendu :
//   1. `MemoDirectPreviewScenarioPanel` (ici) + identité stable de `openGroups` ;
//   2. `railContent` — le JSX du rail, mémoïsé sur ses vraies entrées ;
//   3. `PreviewCard` — mémoïsé, et ses deux rendus de gabarit mémoïsés.
const MemoDirectPreviewScenarioPanel = memo(DirectPreviewScenarioPanel);

// Identité stable pour « aucun groupe à ouvrir » (évite un tableau neuf).
const NO_SCENARIO_GROUPS = [];

// Identité stable pour « ce gabarit n'a pas de défaut builder » (idem : un objet
// littéral recréé à chaque rendu casserait la mémoïsation de DefaultReference).
const EMPTY_BUILDER_DEFAULT = {};

// ── Catalogue des variables « Info voyageur » (menu déroulant) ────────────────
// Les VRAIS tokens du moteur (lib/journey-templates.js) sont PROPRES À CHAQUE
// message (JOURNEY_TEMPLATE_META[slug].vars) et hétérogènes (guestFirstName,
// montantLine, prenom, montant, guest_first_name…). Le menu propose donc, pour le
// message sélectionné, SES variables réelles — libellées via VAR_LABELS_FR (dico
// partagé, on n'invente aucun nom) — rangées sous les groupes de la maquette de
// référence (docs/handoff/maquettes/maquette-direct-fbz.html) via la carte
// statique TOKEN_GROUP ci-dessous. Ainsi tout token inséré est TOUJOURS valide
// (la validation serveur refuse les inconnus).
const GROUP_ORDER = [
  "Voyageur & séjour",
  "Montants",
  GROUPE_REFERENCES,
  "Liens",
  "Conciergerie",
  // PARTIE B / B2 (ADR-272) — drapeaux de conditions {#if …} (formulaire,
  // caution, remboursement, règles logement). Sans rendu texte.
  "Statut & conditions",
];

// Classe chaque token réel du moteur sous un groupe de la maquette (statique,
// déterministe — pas d'interprétation par message). Un token absent tombe dans
// « Voyageur & séjour » par défaut.
const TOKEN_GROUP = {
  // Voyageur & séjour
  guestFirstName: "Voyageur & séjour",
  prenom: "Voyageur & séjour",
  guest_first_name: "Voyageur & séjour",
  guestLastName: "Voyageur & séjour",
  guestEmail: "Voyageur & séjour",
  guestPhone: "Voyageur & séjour",
  logementNom: "Voyageur & séjour",
  logement: "Voyageur & séjour",
  logement_name: "Voyageur & séjour",
  from: "Voyageur & séjour",
  startDate: "Voyageur & séjour",
  date_arrivee: "Voyageur & séjour",
  to: "Voyageur & séjour",
  endDate: "Voyageur & séjour",
  dates: "Voyageur & séjour",
  nbNuits: "Voyageur & séjour",
  nbVoyageurs: "Voyageur & séjour",
  checkinTime: "Voyageur & séjour",
  checkoutTime: "Voyageur & séjour",
  checkin_date: "Voyageur & séjour",
  checkout_date: "Voyageur & séjour",
  // Montants
  montantLine: "Montants",
  montantTotalLine: "Montants",
  montant: "Montants",
  tarifLine: "Montants",
  cautionLine: "Montants",
  cautionAmount: "Montants",
  cautionAvailableFromLabel: "Montants",
  date_caution: "Montants",
  // Références & annulation
  requestIdShort: GROUPE_REFERENCES,
  request_ref: GROUPE_REFERENCES,
  requestId: GROUPE_REFERENCES,
  signatureId: GROUPE_REFERENCES,
  insertedId: GROUPE_REFERENCES,
  politiqueAnnulationText: GROUPE_REFERENCES,
  politiqueAnnulation: GROUPE_REFERENCES,
  promoLine: GROUPE_REFERENCES,
  motifLine: GROUPE_REFERENCES,
  messageSafe: GROUPE_REFERENCES,
  manquants: GROUPE_REFERENCES,
  manquants_en: GROUPE_REFERENCES,
  // Liens
  journeyUrl: "Liens",
  lien: "Liens",
  lien_livret: "Liens",
  avis_google_url: "Liens",
  // Conciergerie
  hostLabel: "Conciergerie",
  contactInfoLine: "Conciergerie",
  // ── PARTIE B / B2 (ADR-272) — catalogue Direct (alias FR + variables créées +
  // drapeaux de conditions). Miroir de lib/direct-vars-catalog.js.
  // Voyageur & séjour
  nb_nuits: "Voyageur & séjour",
  nb_voyageurs: "Voyageur & séjour",
  guest_email: "Voyageur & séjour",
  guest_phone: "Voyageur & séjour",
  guest_last_name: "Voyageur & séjour",
  heure_arrivee: "Voyageur & séjour",
  heure_depart: "Voyageur & séjour",
  ville: "Voyageur & séjour",
  date_veille_arrivee: "Voyageur & séjour",
  heure_arrivee_voyageur: "Voyageur & séjour",
  // Montants
  caution_montant: "Montants",
  montant_rembourse: "Montants",
  montant_sejour: "Montants",
  // #21 « Dépôt de garantie restitué » — mêmes groupes que DIRECT_VAR_CATALOG.
  montant_preleve: "Montants",
  montant_libere: "Montants",
  // Références & annulation
  motif_prelevement: GROUPE_REFERENCES,
  reference_demande: GROUPE_REFERENCES,
  politique_annulation: GROUPE_REFERENCES,
  motif_rejet: GROUPE_REFERENCES,
  motif_annulation: GROUPE_REFERENCES,
  code_promo: GROUPE_REFERENCES,
  promo_pourcentage: GROUPE_REFERENCES,
  delai_reponse: GROUPE_REFERENCES,
  delai_paiement: GROUPE_REFERENCES,
  date_limite_paiement: GROUPE_REFERENCES,
  // Liens
  lien_sejour: "Liens",
  lien_site_direct: "Liens",
  lien_admin_demande: "Liens",
  // Conciergerie
  message_voyageur: "Conciergerie",
  email_contact: "Conciergerie",
  telephone_contact: "Conciergerie",
  // Statut & conditions (drapeaux {#if …})
  formulaire_manquant: "Statut & conditions",
  formulaire_rempli: "Statut & conditions",
  caution_manquante: "Statut & conditions",
  caution_recue: "Statut & conditions",
  caution_empreinte: "Statut & conditions",
  caution_debit: "Statut & conditions",
  caution_deposee: "Statut & conditions",
  animaux_non: "Statut & conditions",
  fumeur_non: "Statut & conditions",
  motif_affiche: "Statut & conditions",
  remboursement_effectue: "Statut & conditions",
  aucun_remboursement: "Statut & conditions",
  remboursement_total: "Statut & conditions",
  remboursement_partiel: "Statut & conditions",
  remboursement_aucun: "Statut & conditions",
};

// Construit les groupes de variables pour un message donné, à partir de ses vars
// réelles (builder.vars), au FORMAT du SegmentEditor partagé (M12 : même éditeur
// à pastilles que Plateformes) : [{ category, variables:[{key, label}] }] non
// vides, dans l'ordre GROUP_ORDER. Le picker « + Info voyageur » ne propose donc
// QUE les variables valides du gabarit édité (la validation serveur refuse les
// inconnues — une variable invalide insérée partirait en clair dans l'email).
function buildVarGroups(vars) {
  const list = Array.isArray(vars) ? vars : [];
  const byGroup = new Map();
  for (const name of list) {
    const grp = TOKEN_GROUP[name] || "Voyageur & séjour";
    if (!byGroup.has(grp)) byGroup.set(grp, []);
    byGroup.get(grp).push({
      key: name,
      label: VAR_LABELS_FR[name] || name,
    });
  }
  return GROUP_ORDER.filter((g) => byGroup.has(g)).map((g) => ({
    category: g,
    variables: byGroup.get(g),
  }));
}

// Libellé d'une pastille / entrée de picker côté Direct : le dico du parcours
// (VAR_LABELS_FR) fait foi — une variable hors dico (vieux contenu, token
// inconnu) affiche son nom brut en pastille neutre (jamais de crash, jamais
// vide). Passé au SegmentEditor via la prop labelFor.
function directVarLabel(name) {
  return VAR_LABELS_FR[name] || name;
}

// ── B5 : APERÇU INTERACTIF des blocs conditionnels {#if …}…{/if} ──────────────
// B4 dépliait les blocs conditionnels (« tout affiché », toutes conditions
// supposées vraies). B5 les ÉVALUE réellement : le panneau de scénario
// (DirectPreviewScenarioPanel) simule l'état d'une réservation, en dérive les
// drapeaux du catalogue B2, et `renderDirectPreview` (src/lib/direct-preview-engine)
// rejoue les DEUX passes du serveur — la même `evaluateConditions` et le même
// résolveur de contexte Direct que lib/direct-rich-render.js.
//
// L'interpolation {token} (déplacée telle quelle) et la passe conditionnelle
// vivent désormais dans src/lib/direct-preview-engine.js — home unique client,
// parité byte verrouillée par src/lib/direct-preview-parity.test.js.

// Découpe un corps de mail (texte builder) en paragraphes pour l'affichage.
function toParagraphs(text) {
  if (typeof text !== "string") return [];
  return text.split("\n");
}

function recipientRole(m) {
  return m && m.recipient === "admin" ? "ADM" : "VOY";
}

// Message créé par l'admin (3B) — éditable/renommable/supprimable, pas de
// builder d'origine, non branché à un envoi.
function isManuelMsg(m) {
  return !!m && m.trigger_kind === "manuel";
}

// ── Réordonnancement (3B) : calcul pur de la nouvelle liste de slugs ─────────
// Déplace `fromSlug` À LA PLACE de `toSlug` (insertion à l'index cible) dans la
// liste ordonnée d'une catégorie. Pur, testé unitairement.
export function computeReorderedSlugs(slugs, fromSlug, toSlug) {
  const list = [...slugs];
  const from = list.indexOf(fromSlug);
  const to = list.indexOf(toSlug);
  if (from === -1 || to === -1 || from === to) return slugs;
  list.splice(from, 1);
  list.splice(to, 0, fromSlug);
  return list;
}

// ── Métadonnées de déclencheur PAR SLUG (affichage, maquette de référence) ─────
// Mot pour mot depuis la maquette (docs/handoff/maquettes/maquette-direct-fbz.html,
// champs `tt`/`cond`). L'affichage du bloc « Condition d'envoi » NE LIT JAMAIS le
// libellé technique de la base (`trigger_condition_label`) : on affiche ce QUAND
// court + cette condition « métier ». Cosmétique en lecture seule.
//   quand : libellé court de la ligne QUAND.
//   cond  : texte de la ligne SEULEMENT SI (voyageur uniquement), null = aucune.
const TRIGGER_META = {
  request_received_voyageur: {
    quand: "Quand une demande directe arrive",
    cond: null,
  },
  request_received_admin: {
    quand: "Quand une demande directe arrive",
    cond: null,
  },
  validated_voyageur: { quand: "Quand vous acceptez la demande", cond: null },
  rejected_voyageur: { quand: "Quand vous refusez la demande", cond: null },
  // LOT L5a — les 2 derniers textes rapatriés (affichage seul, sys verrouillés).
  request_expired_voyageur: {
    quand: "Quand une demande reste 48 h sans réponse",
    cond: null,
  },
  rejected_motif_defaut: {
    quand: "Inséré dans « Demande rejetée » (si aucun motif public)",
    cond: "Seul le texte est utilisé : l'objet est ignoré",
  },
  contract_signed_voyageur: {
    quand: "Quand le voyageur signe le contrat",
    cond: null,
  },
  contract_signed_admin: {
    quand: "Quand le voyageur signe le contrat",
    cond: null,
  },
  paid_stay_voyageur: { quand: "Quand le séjour est payé", cond: null },
  paid_stay_admin: { quand: "Quand le séjour est payé", cond: null },
  // LOT B (caducité 48 h post-signature, décision 2026-08-01) — expiration
  // d'une réservation signée jamais payée (cron quotidien 06h UTC).
  payment_expired_voyageur: {
    quand: "Quand une réservation signée reste 48 h sans paiement",
    cond: "Aucun paiement en cours ni déjà fait",
  },
  confirmed_voyageur: { quand: "Quand la caution est reçue", cond: null },
  caution_received_admin: { quand: "Quand la caution est reçue", cond: null },
  caution_reminder_j2: {
    quand: "2 jours avant l'arrivée",
    cond: "Caution ou formulaire encore manquant",
  },
  caution_reminder_j1: {
    quand: "1 jour avant l'arrivée",
    cond: "Caution ou formulaire encore manquant",
  },
  caution_reminder_j0: {
    quand: "Le jour de l'arrivée",
    cond: "Caution ou formulaire encore manquant",
  },
  depart_voyageur: { quand: "Le jour du départ", cond: null },
  // Lot « registre à jour » — les 2 gabarits qui n'avaient aucune entrée (le
  // panneau affichait « — » faute de descriptif).
  caution_restituee: {
    quand: "Quand le dépôt de garantie est réglé, après le départ",
    cond: "Le montant annoncé n'apparaît que s'il est connu — sinon le message part sans bloc montant",
  },
  arrival_instructions_j1: {
    quand: "La veille de l'arrivée, au passage du soir",
    cond: "Réservation confirmée dont l'arrivée est le lendemain",
  },
  // Lot SUIVI ADMIN (maquette, verbatim tt/cond).
  notif_admin_arrivee_jour_j: {
    quand: "Le jour d'une arrivée directe",
    cond: null,
  },
  notif_admin_caution_relance: {
    quand: "1 jour avant l'arrivée",
    cond: "Caution encore due",
  },
  // CHANTIER 0.1-A — fragments éditables (corps seul, pattern rejected_motif_defaut).
  fragment_caution_line: {
    quand: "Inséré dans « Demande reçue — voyageur » (si caution configurée)",
    cond: "Seul le texte est utilisé : l'objet est ignoré",
  },
  fragment_contact_line: {
    quand: "Inséré dans « Demande reçue — voyageur » (coordonnées de contact)",
    cond: "Seul le texte est utilisé : l'objet est ignoré",
  },
  fragment_manquants_caution: {
    quand: "Inséré dans les rappels J-2 / J-1 / jour J (si la caution manque)",
    cond: "Seul le texte est utilisé : l'objet est ignoré",
  },
  fragment_manquants_formulaire: {
    quand:
      "Inséré dans les rappels J-2 / J-1 / jour J (si le formulaire manque)",
    cond: "Seul le texte est utilisé : l'objet est ignoré",
  },
};

// Les 4 SEULS messages « argent » : ruban CHEMIN PAIEMENT (icône shield-lock).
const MONEY_SLUGS = new Set([
  "paid_stay_voyageur",
  "paid_stay_admin",
  "confirmed_voyageur",
  "caution_received_admin",
]);

// Messages du registre qu'AUCUN émetteur n'appelle aujourd'hui : le flash de
// save et la note d'heure le disent honnêtement (règle « texte d'aide = état
// réel »). C'est un constat de CÂBLAGE, pas un jugement sur le texte — ces
// messages restent pleinement éditables.
//
// ⚠️ Liste à RELIRE dès qu'un émetteur est ajouté ou retiré :
//   • `notif_admin_arrivee_jour_j` / `notif_admin_caution_relance` en sont
//     SORTIS (PR #1118) : ADMIN_NOTIF_PASSES, dans lib/direct-journey-sweep.js,
//     les envoie au passage du matin — et sans gate MESSAGES_DRY_RUN (convention
//     des envois admin de ce fichier), donc ils partent avec les flags actuels ;
//   • `caution_restituee` y ENTRE : lib/caution-restitution-dispatch.js porte la
//     mécanique #21 mais n'a AUCUN point d'appel en production (le module se
//     décrit lui-même comme « prêt à être branché »), et resterait de toute
//     façon inerte tant que CAUTION_RESTITUTION_MESSAGE_LIVE n'est pas 'true'.
const NOT_WIRED_SLUGS = new Set(["caution_restituee"]);

// ── LOT « rangement » — fragments = morceaux réutilisables, pas des messages ──
// Ces textes ne partent JAMAIS seuls : ils sont injectés dans un autre e-mail du
// parcours (ligne caution / ligne contact de « Demande reçue », libellés des
// rappels). Ils vivent déjà dans leur propre catégorie en base ; ici on décide
// seulement de ne PAS les rendre comme une catégorie de messages ordinaire.
// RENDU CLIENT PUR — aucune écriture, aucune modification de la catégorie base.
const FRAGMENT_CATEGORY_SLUGS = new Set(["fragments"]);

// État d'ouverture de la section, mémorisé pour la SESSION (même pattern que
// les cartes repliables de l'onglet Instructions, cf. src/tabs/logements/
// LivretTab.jsx : lecture au montage + écriture à chaque changement, try/catch
// car sessionStorage peut être indisponible). Défaut = FERMÉ.
const FRAGMENTS_OPEN_KEY = "fbz8-direct-fragments-open";
function readFragmentsOpen() {
  try {
    return sessionStorage.getItem(FRAGMENTS_OPEN_KEY) === "1";
  } catch (_) {
    return false; // sessionStorage indisponible → fermé, comme au premier passage
  }
}

// ── Heures des 6 messages temporels — ÉTAT RÉEL, pas la maquette ─────────────
//
// Règle permanente : « le texte d'aide décrit l'état actuel, jamais une
// intention ». Une heure affichée doit donc être l'heure à laquelle le message
// PART VRAIMENT. Ces valeurs sont CONSTATÉES dans le code d'envoi — toute
// évolution du planning doit être répercutée ici.
//
//   • PASSAGE DU MATIN — `api/cron/direct-journey-daily`, planifié `0 9 * * *`
//     dans vercel.json (UTC) et SANS garde horaire côté handler → 10h00 à Paris
//     en heure d'hiver, 11h00 en heure d'été. Passes concernées, dans
//     lib/direct-journey-sweep.js : `runCautionReminders` phase 'morning'
//     (CAUTION_REMINDER_PASSES j2 = J-3 et j0 = jour J), `runDepartureMessage`
//     (depart_voyageur) et `runAdminJourneyNotifications` phase 'morning'
//     (ADMIN_NOTIF_PASSES : arrivee_jour_j ET caution_relance — MÊME boucle,
//     donc MÊME minute).
//   • PASSAGE DU SOIR — `api/cron/direct-journey-evening`, planifié DEUX fois
//     (`0 18` + `0 19` UTC) avec la garde `_parisHour(now) !== 20 → no-op` :
//     l'envoi a lieu à 20h00 Paris, été comme hiver. Passe concernée :
//     `runCautionReminders` phase 'evening' (CAUTION_REMINDER_PASSES j1 = J-1).
//
// ⚠️ ÉCARTS CORRIGÉS (l'interface annonçait des heures que le code ne fait pas) :
//   - `notif_admin_caution_relance` affichait 18:00 alors que PR #1118 l'a câblé
//     au passage du MATIN de J-1, délibérément : son texte annonce que sans la
//     caution aucune instruction ne partira « ce soir à 20h » — une alerte
//     envoyée à 18h serait trop tardive pour réagir ;
//   - `notif_admin_arrivee_jour_j` affichait 08:00 alors qu'il tire dans la MÊME
//     boucle que la relance, à la même minute (deux heures différentes pour un
//     seul et même passage : impossible) ;
//   - `caution_reminder_j1` affichait 09:00 alors qu'il part du passage du SOIR ;
//   - `caution_reminder_j2` / `caution_reminder_j0` affichaient 09:00, qui est
//     l'heure UTC du cron, pas l'heure de Paris ;
//   - `depart_voyageur` affichait 11:00 (valeur de maquette qui ne coïncidait
//     avec la réalité qu'en heure d'été).
//
// L'heure du matin est ambiguë par construction (le cron est calé en UTC, pas
// sur l'heure légale française) : on affiche l'heure d'HIVER et la note sous le
// sélecteur dit le décalage d'été noir sur blanc. Caler le cron du matin sur une
// heure de Paris fixe — comme le fait déjà celui du soir — serait la vraie
// correction de fond ; elle touche vercel.json et sort du périmètre de ce lot.
const MORNING_PASS_HOUR = "10:00";
const EVENING_PASS_HOUR = "20:00";

// Passage RÉEL qui envoie chaque message temporel (source unique : l'heure
// affichée et la note sous le sélecteur en découlent toutes les deux, elles ne
// peuvent donc pas diverger).
const TEMPORAL_PASSAGE = {
  caution_reminder_j2: "morning",
  caution_reminder_j1: "evening",
  caution_reminder_j0: "morning",
  depart_voyageur: "morning",
  notif_admin_arrivee_jour_j: "morning",
  notif_admin_caution_relance: "morning",
  // 7e entrée : `arrival_instructions_j1` n'avait AUCUNE heure par défaut (il
  // retombait sur le 09:00 générique). Il part de `runArrivalInstructions`,
  // appelé par api/cron/direct-journey-evening — donc à 20h00 Paris, comme le
  // rappel caution J-1 du même passage.
  arrival_instructions_j1: "evening",
};

// Phrase de passage affichée sous le sélecteur d'heure — état RÉEL constaté.
const PASSAGE_NOTE = {
  morning:
    "Envoi du matin : part à 10 h en hiver et à 11 h en été (heure de Paris).",
  evening:
    "Passage du soir : 20h00 à Paris, été comme hiver (l'heure de Paris est vérifiée avant chaque envoi).",
};

// Heures par défaut affichées tant qu'aucune heure n'est stockée en base
// (trigger_condition.hour) — chip « défaut établi ». DÉRIVÉES du passage réel.
const DEFAULT_HOURS = Object.keys(TEMPORAL_PASSAGE).reduce((acc, slug) => {
  acc[slug] =
    TEMPORAL_PASSAGE[slug] === "evening"
      ? EVENING_PASS_HOUR
      : MORNING_PASS_HOUR;
  return acc;
}, {});

// ── Vue principale ────────────────────────────────────────────────────────────
export default function DirectMessagesView() {
  const isMobile = useIsMobile(); // < 768 : tout empilé
  const isNarrow = useIsMobile(1180); // < 1180 : éditeur + aperçu empilés

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [categories, setCategories] = useState([]);
  const [builderBySlug, setBuilderBySlug] = useState({});
  const [exampleVars, setExampleVars] = useState(null);
  // B5 — APERÇU INTERACTIF : état du panneau « Simuler une réservation ».
  // ⚠️ STATE SÉPARÉ, JAMAIS DANS `draft` : le scénario est cosmétique. S'il
  // entrait dans le brouillon, `dirty` s'allumerait au premier clic (bouton
  // « Enregistrer » actif sur un message non modifié + garde brouillon
  // déclenchée au changement de message). Régression silencieuse à ne jamais
  // réintroduire — couverte par un test dédié.
  const [scenario, setScenario] = useState(DEFAULT_DIRECT_SCENARIO);
  const [selectedSlug, setSelectedSlug] = useState(null);
  const [search, setSearch] = useState("");
  // Lot visuel : filtre par destinataire (Tous / Voyageur / Admin) — affichage
  // SEULEMENT (maquette Plateformes, segmenté sous la recherche).
  const [roleFilter, setRoleFilter] = useState("all"); // 'all' | 'VOY' | 'ADM'
  // LOT « rangement » : section « Morceaux réutilisables » — FERMÉE par défaut,
  // ouverture mémorisée pour la session (pattern des cartes de l'onglet
  // Instructions). Affichage pur : n'entre JAMAIS dans `draft`.
  const [fragmentsOpen, setFragmentsOpen] = useState(readFragmentsOpen);
  useEffect(() => {
    try {
      sessionStorage.setItem(FRAGMENTS_OPEN_KEY, fragmentsOpen ? "1" : "0");
    } catch (_) {
      /* sessionStorage indisponible → l'état reste local à la page */
    }
  }, [fragmentsOpen]);
  // LOT L1 : conditions RÉELLES du moteur (sweep) — null tant que non chargées
  // ou en échec → l'UI retombe sur les descriptifs statiques (best-effort).
  const [engineConds, setEngineConds] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const [struct, journey, conds] = await Promise.all([
          fetchDirectMessages(),
          fetchDirectMessagesBuilders(),
          // L1 best-effort : échec → null, l'onglet vit sans (pas de throw).
          fetchDirectConditions().catch((e) => {
            console.error("[direct] conditions moteur illisibles:", e.message);
            return null;
          }),
        ]);
        if (!alive) return;
        const cats = (struct.categories || []).filter(Boolean)
          .map((c) => ({ ...c, messages: (c.messages || []).filter(Boolean) }));
        const map = {};
        (journey.items || []).forEach((it) => {
          map[it.slug] = it;
        });
        setCategories(cats);
        setBuilderBySlug(map);
        setExampleVars(journey.exampleVars || null);
        setEngineConds(conds && conds.ok ? conds : null);
        // Sélection par défaut : 1er gabarit de la 1re catégorie non vide.
        const firstMsg = cats.flatMap((c) => c.messages || []).find(Boolean);
        setSelectedSlug((prev) => prev || (firstMsg && firstMsg.slug) || null);
      } catch (e) {
        if (alive) setError(e.message || "Erreur de chargement");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const allMessages = useMemo(
    () => categories.flatMap((c) => c.messages || []),
    [categories],
  );

  const selected = useMemo(
    () => allMessages.find((m) => m.slug === selectedSlug) || null,
    [allMessages, selectedSlug],
  );

  // B5 — contexte d'aperçu = variables d'exemple du serveur + tokens du scénario
  // + drapeaux DÉRIVÉS (jamais posés à la main : cf. direct-preview-scenario.js).
  // 100 % local, aucun appel réseau — `exampleVars` est déjà en mémoire.
  const previewVars = useMemo(
    () => buildDirectPreviewVars(exampleVars, scenario),
    [exampleVars, scenario],
  );

  // ── Édition (sous-lot 3A + CORRECTION 1) ───────────────────────────────────
  // Contenu de départ de l'éditeur pour le message sélectionné :
  //   is_edited=true → le contenu base (subject_fr/body_fr, avec les {tokens}) ;
  //   is_edited=false + défaut TOKENISÉ disponible (gabarit « plat ») → on POSE
  //                    la chaîne canonique à {tokens} (byte-identique au builder,
  //                    test golden) : l'admin part du texte réel, plus d'une boîte
  //                    vide (CORRECTION 1). On NE pose JAMAIS le défaut rendu avec
  //                    des valeurs d'EXEMPLE (Camille, /r/EXEMPLE…) : cela figerait
  //                    des littéraux (règle « stocker les tokens, pas les exemples »).
  //   is_edited=false + gabarit COMPOSÉ (default_tokenized null) → VIDE (actuel) ;
  //                    le défaut est montré en RÉFÉRENCE lecture seule, l'admin
  //                    compose avec les vraies variables (menu « + Info voyageur »).
  const builder = selected ? builderBySlug[selected.slug] : null;
  const selectedIsManuel = isManuelMsg(selected);

  // Variables proposées à un message MANUEL (3B) : l'union des variables de tous
  // les gabarits (même référentiel que la validation serveur ALL_DECLARED_VARS).
  const manualVars = useMemo(() => {
    const s = new Set();
    Object.values(builderBySlug).forEach((b) =>
      (b.vars || []).forEach((v) => s.add(v)),
    );
    return [...s];
  }, [builderBySlug]);
  const EMPTY_DRAFT = {
    subject: "",
    body: "",
    subject_en: "",
    body_en: "",
    // B4-ext — ES persisté (colonnes subject_es/body_es, cf. EditorCard).
    subject_es: "",
    body_es: "",
    name: "",
  };
  const baseline = useMemo(() => {
    if (!selected)
      return {
        subject: "",
        body: "",
        subject_en: "",
        body_en: "",
        subject_es: "",
        body_es: "",
        name: "",
      };
    // Message MANUEL (3B) : la base est la SEULE source (pas de builder) — on
    // réhydrate toujours son contenu + son nom (renommable). Jamais d'EN
    // (bilingue=false).
    if (isManuelMsg(selected)) {
      return {
        subject: selected.subject_fr || "",
        body: selected.body_fr || "",
        subject_en: "",
        body_en: "",
        subject_es: "",
        body_es: "",
        name: selected.name || "",
      };
    }
    if (!selected.is_edited) {
      // CORRECTION 1 (cadrage archi-corrections-direct, P1=A2 / P2=tokenisation) :
      // quand le gabarit n'est pas encore personnalisé, on POSE le DÉFAUT TOKENISÉ
      // (chaîne canonique à {tokens}, byte-identique au builder — test golden CI)
      // au lieu d'un éditeur vide. L'admin part du texte réel avec ses {tokens},
      // pas d'une boîte vide à recomposer. « Revenir au défaut » (reset) reste
      // possible et l'UI re-pose ce défaut. handleSave POST ce texte tel quel →
      // is_edited=true, dbRow = défaut tokenisé → envoi byte-identique au builder.
      // Gabarits COMPOSÉS : default_tokenized absent (null) → on garde l'éditeur
      // vide (comportement actuel) — tokenisation réservée à un lot copy/produit.
      const tok = builder && builder.default_tokenized;
      if (tok && (tok.subject_fr || tok.body_fr)) {
        return {
          subject: tok.subject_fr || "",
          body: tok.body_fr || "",
          subject_en: tok.subject_en || "",
          body_en: tok.body_en || "",
          subject_es: tok.subject_es || "",
          body_es: tok.body_es || "",
          name: "",
        };
      }
      return {
        subject: "",
        body: "",
        subject_en: "",
        body_en: "",
        subject_es: "",
        body_es: "",
        name: "",
      };
    }
    return {
      subject: selected.subject_fr || "",
      body: selected.body_fr || "",
      // Lot L : réhydrate l'EN stocké (bilingues uniquement — NULL sinon).
      subject_en: selected.subject_en || "",
      body_en: selected.body_en || "",
      // B4-ext : ES réhydraté depuis la base (MESSAGE_COLUMNS le renvoie) et
      // ré-écrit au save, comme l'EN.
      subject_es: selected.subject_es || "",
      body_es: selected.body_es || "",
      name: "",
    };
  }, [selected, builder]);

  const [draft, setDraft] = useState(EMPTY_DRAFT);
  // M12 — remountNonce des SegmentEditor (même pattern que MessageEditor
  // Plateformes) : l'éditeur segmenté parse la valeur UNE fois au montage —
  // toute mutation PROGRAMMATIQUE du brouillon (resync baseline, reset) doit
  // forcer un remontage, sinon l'écran resterait périmé vs le state. Les
  // frappes utilisateur, elles, ne remontent jamais (caret préservé).
  const [editorNonce, setEditorNonce] = useState(0);
  // Lot L : langue active de l'éditeur (FR par défaut, reset au changement de
  // message). Vit ICI (pas dans EditorCard) car l'aperçu la suit aussi.
  const [activeLang, setActiveLang] = useState("fr");
  const [saving, setSaving] = useState(false);
  const [saveErr, setSaveErr] = useState(null);
  const [flash, setFlash] = useState(null); // 'saved' | 'reset' | null

  // Lot L / B4 : le texte AFFICHÉ dans l'aperçu suit la langue active — brouillon
  // de la langue, sinon défaut builder de la langue, sinon repli FR. Calculé ici
  // (et non plus en ligne dans le JSX) car il sert AUSSI à ouvrir les BONS
  // groupes du panneau de scénario : seuls ceux dont un drapeau `{#if}` est
  // réellement utilisé par CE gabarit sont dépliés (QA #1095 — panneau compact,
  // aperçu visible sans scroller).
  const previewSubject =
    activeLang === "en"
      ? draft.subject_en ||
        (builder?.default?.subject_en ?? "") ||
        draft.subject ||
        (builder?.default?.subject_fr ?? "")
      : activeLang === "es"
        ? draft.subject_es ||
          (builder?.default?.subject_es ?? "") ||
          draft.subject ||
          (builder?.default?.subject_fr ?? "")
        : draft.subject || (builder?.default?.subject_fr ?? "");
  const previewBody =
    activeLang === "en"
      ? draft.body_en ||
        (builder?.default?.body_en ?? "") ||
        draft.body ||
        (builder?.default?.body_fr ?? "")
      : activeLang === "es"
        ? draft.body_es ||
          (builder?.default?.body_es ?? "") ||
          draft.body ||
          (builder?.default?.body_fr ?? "")
        : draft.body || (builder?.default?.body_fr ?? "");
  // PERF : `scenarioGroupsForTexts` renvoie un TABLEAU NEUF à chaque frappe,
  // même quand la liste des groupes est rigoureusement la même — ce qui
  // re-rendait le panneau de scénario (420 lignes) pour rien à chaque caractère.
  // On passe par une CLÉ de contenu : tant que les groupes ne changent pas, la
  // référence du tableau ne change pas non plus. Rendu identique.
  // (Séparateur U+0000 : impossible dans un libellé de groupe.)
  const scenarioOpenGroupsKey = useMemo(
    () => scenarioGroupsForTexts([previewSubject, previewBody]).join("\u0000"),
    [previewSubject, previewBody],
  );
  const scenarioOpenGroups = useMemo(
    () =>
      scenarioOpenGroupsKey === ""
        ? NO_SCENARIO_GROUPS
        : scenarioOpenGroupsKey.split("\u0000"),
    [scenarioOpenGroupsKey],
  );

  // (Re)synchronise le brouillon sur la baseline (au changement de message ET
  // après un save/reset qui met à jour la ligne). Deux protections :
  //   - on n'efface l'erreur / le flash QUE sur un vrai changement de message ;
  //   - on ne resynchronise que si les VALEURS de la baseline changent (pessimist
  //     lot C) : un réglage statut/canal recrée la ligne (nouvelle référence,
  //     mêmes subject/body) — sans cette garde, l'effet écraserait silencieusement
  //     un brouillon en cours de frappe. Un save (baseline = draft) ou un reset
  //     (baseline vidée) changent les valeurs → resync normal.
  const prevSlugRef = useRef(null);
  const lastBaselineRef = useRef({
    subject: "",
    body: "",
    subject_en: "",
    body_en: "",
    // B4-ext : l'ES est PERSISTÉ — il doit entrer dans la garde `valuesChanged`,
    // sinon un save ES ne resynchroniserait pas (ou une saisie en cours serait
    // écrasée par un patch de réglage statut/canal).
    subject_es: "",
    body_es: "",
    name: "",
  });
  useEffect(() => {
    const slugChanged = prevSlugRef.current !== selectedSlug;
    const valuesChanged =
      baseline.subject !== lastBaselineRef.current.subject ||
      baseline.body !== lastBaselineRef.current.body ||
      baseline.subject_en !== lastBaselineRef.current.subject_en ||
      baseline.body_en !== lastBaselineRef.current.body_en ||
      baseline.subject_es !== lastBaselineRef.current.subject_es ||
      baseline.body_es !== lastBaselineRef.current.body_es ||
      baseline.name !== lastBaselineRef.current.name;
    if (slugChanged || valuesChanged) {
      setDraft(baseline);
      // M12 : setDraft programmatique → remontage des SegmentEditor (leur
      // arbre est parsé au montage). Jamais bumpé pendant la frappe (un
      // onChange utilisateur rend draft === baseline faux mais ne repasse
      // pas ici : baseline inchangée).
      setEditorNonce((n) => n + 1);
      if (slugChanged) {
        setSaveErr(null);
        setFlash(null);
        setActiveLang("fr"); // lot L : retour au FR au changement de message
        prevSlugRef.current = selectedSlug;
      }
    }
    lastBaselineRef.current = baseline;
  }, [selectedSlug, baseline]);

  const dirty =
    draft.subject !== baseline.subject ||
    draft.body !== baseline.body ||
    draft.subject_en !== baseline.subject_en ||
    draft.body_en !== baseline.body_en ||
    draft.subject_es !== baseline.subject_es ||
    draft.body_es !== baseline.body_es ||
    (selectedIsManuel && draft.name !== baseline.name);

  // Met à jour la ligne en mémoire après une écriture réussie (badge « Modifié »,
  // réhydratation cohérente sans recharger).
  const patchMessage = (slug, patch) => {
    setCategories((cats) =>
      cats.map((c) => ({
        ...c,
        messages: (c.messages || []).map((mm) =>
          mm.slug === slug ? { ...mm, ...patch } : mm,
        ),
      })),
    );
  };

  const handleSave = async () => {
    if (!selected || !dirty || saving) return;
    setSaving(true);
    setSaveErr(null);
    setFlash(null);
    try {
      const content = { subject_fr: draft.subject, body_fr: draft.body };
      // Renommage (3B) : uniquement pour un message manuel.
      if (selectedIsManuel && draft.name.trim())
        content.name = draft.name.trim();
      // Lot L : EN transmis uniquement pour les bilingues (contrat serveur
      // tout-ou-rien : les deux champs, ou aucun → EN remis à NULL).
      if (selected.bilingue) {
        content.subject_en = draft.subject_en;
        content.body_en = draft.body_en;
        // B4-ext : l'ES est désormais PERSISTÉ (mêmes règles que l'EN —
        // bilingues uniquement, contrat serveur tout-ou-rien).
        content.subject_es = draft.subject_es;
        content.body_es = draft.body_es;
      }
      await saveDirectMessage(selected.slug, content);
      patchMessage(selected.slug, {
        subject_fr: draft.subject || null,
        body_fr: draft.body || null,
        is_edited: true,
        ...(content.name ? { name: content.name } : {}),
        ...(selected.bilingue
          ? {
              subject_en: draft.subject_en.trim() ? draft.subject_en : null,
              body_en: draft.body_en.trim() ? draft.body_en : null,
              subject_es: draft.subject_es.trim() ? draft.subject_es : null,
              body_es: draft.body_es.trim() ? draft.body_es : null,
            }
          : {}),
      });
      setFlash("saved");
    } catch (e) {
      setSaveErr(e.message || "Enregistrement échoué");
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!selected || saving) return;
    setSaving(true);
    setSaveErr(null);
    setFlash(null);
    try {
      await resetDirectMessage(selected.slug);
      patchMessage(selected.slug, {
        subject_fr: null,
        body_fr: null,
        subject_en: null,
        body_en: null,
        is_edited: false,
      });
      // Vidage EXPLICITE du brouillon : si le message n'avait jamais été
      // sauvegardé (baseline déjà vide), l'effet baseline ne détecterait aucun
      // changement de valeurs et laisserait la saisie en cours dans les champs.
      setDraft(EMPTY_DRAFT);
      // M12 : vidage programmatique → remontage des SegmentEditor (cf. nonce).
      setEditorNonce((n) => n + 1);
      setFlash("reset");
    } catch (e) {
      setSaveErr(e.message || "Retour au défaut échoué");
    } finally {
      setSaving(false);
    }
  };

  // ── Sous-lot 3B : créer / supprimer / réordonner + garde brouillon ─────────
  const [actionErr, setActionErr] = useState(null); // erreurs create/delete/reorder
  const [addingCat, setAddingCat] = useState(null); // catégorie dont l'input « Ajouter » est ouvert
  // Garde-fou brouillon non sauvé : action en attente de confirmation quand
  // `dirty`. { type:'select', slug } ou { type:'create', categoryId, name }.
  const [pendingAction, setPendingAction] = useState(null);

  // Sélectionne un message — passe par le garde-fou si des modifs non
  // enregistrées seraient perdues.
  const requestSelect = (slug) => {
    if (slug === selectedSlug) return;
    if (dirty) {
      setPendingAction({ type: "select", slug });
      return;
    }
    setSelectedSlug(slug);
  };

  const doCreate = async (categoryId, name) => {
    setActionErr(null);
    try {
      const { message } = await createDirectMessage(categoryId, name);
      // Insère la ligne créée en fin de sa catégorie + la sélectionne.
      setCategories((cats) =>
        cats.map((c) =>
          c.id === categoryId
            ? { ...c, messages: [...(c.messages || []), message] }
            : c,
        ),
      );
      setAddingCat(null);
      setSelectedSlug(message.slug);
    } catch (e) {
      setActionErr(e.message || "Création échouée");
    }
  };

  // Création — garde-fou brouillon (créer sélectionne le nouveau message, donc
  // perdrait le brouillon courant).
  const requestCreate = (categoryId, name) => {
    if (dirty) {
      setPendingAction({ type: "create", categoryId, name });
      return;
    }
    doCreate(categoryId, name);
  };

  // Confirme / annule l'action en attente (garde-fou brouillon).
  const confirmPending = () => {
    const p = pendingAction;
    setPendingAction(null);
    if (!p) return;
    if (p.type === "select") setSelectedSlug(p.slug);
    if (p.type === "create") doCreate(p.categoryId, p.name);
  };
  const cancelPending = () => setPendingAction(null);

  // Suppression du message MANUEL sélectionné (la confirmation UI vit dans
  // l'éditeur ; le serveur re-vérifie de son côté).
  const handleDelete = async () => {
    if (!selected || !selectedIsManuel || saving) return;
    setSaving(true);
    setSaveErr(null);
    setActionErr(null);
    // Referme un éventuel garde-fou brouillon périmé (l'intention « changer
    // vers Y » n'a plus de sens une fois X supprimé).
    setPendingAction(null);
    try {
      await deleteDirectMessage(selected.slug);
      const deletedSlug = selected.slug;
      setCategories((cats) =>
        cats.map((c) => ({
          ...c,
          messages: (c.messages || []).filter((mm) => mm.slug !== deletedSlug),
        })),
      );
      // Re-sélectionne le 1er message restant.
      setSelectedSlug(null);
    } catch (e) {
      setSaveErr(e.message || "Suppression échouée");
    } finally {
      setSaving(false);
    }
  };

  // Après une suppression (selectedSlug=null), retombe sur le 1er message.
  useEffect(() => {
    if (!loading && !selectedSlug && allMessages.length > 0) {
      setSelectedSlug(allMessages[0].slug);
    }
  }, [loading, selectedSlug, allMessages]);

  // ── Drag-and-drop intra-catégorie (3B) ─────────────────────────────────────
  // drag = { catId, slug } pendant le glisser ; dragOverSlug = cible survolée.
  const [drag, setDrag] = useState(null);
  const [dragOverSlug, setDragOverSlug] = useState(null);
  // Le DnD est désactivé pendant une recherche OU un filtre destinataire
  // (liste filtrée → réordonner serait trompeur). NB : `search` directement
  // (q est défini plus bas).
  const dndEnabled = !search.trim() && roleFilter === "all";

  const handleDrop = async (catId, toSlug) => {
    const d = drag;
    setDrag(null);
    setDragOverSlug(null);
    if (!d || d.catId !== catId || d.slug === toSlug) return;
    const cat = categories.find((c) => c.id === catId);
    if (!cat) return;
    const before = (cat.messages || []).map((m) => m.slug);
    const after = computeReorderedSlugs(before, d.slug, toSlug);
    if (after === before) return;
    // Optimiste : réordonne l'état local, puis confirme côté serveur ; en cas
    // d'échec, on REVIENT à l'ordre précédent + message d'erreur.
    const bySlug = new Map((cat.messages || []).map((m) => [m.slug, m]));
    const reorderCat = (order) =>
      setCategories((cats) =>
        cats.map((c) =>
          c.id === catId
            ? {
                ...c,
                messages: order.map((s) => bySlug.get(s)).filter(Boolean),
              }
            : c,
        ),
      );
    reorderCat(after);
    setActionErr(null);
    try {
      await reorderDirectMessages(catId, after);
    } catch (e) {
      reorderCat(before);
      setActionErr(e.message || "Réordonnancement échoué");
    }
  };

  // ── Lots C+H : statut + canaux + heure réglables (écriture immédiate,
  // optimiste). `hour` (lot H) est traduit en trigger_condition pour l'état
  // local (l'API reçoit { hour }, la base stocke trigger_condition.hour).
  const [settingsBusy, setSettingsBusy] = useState(false);
  const handleSettings = async (patch) => {
    if (!selected || settingsBusy) return;
    const prev = {
      status: selected.status,
      canal: selected.canal,
      trigger_condition: selected.trigger_condition,
    };
    const optimistic = { ...patch };
    if (patch.hour !== undefined) {
      optimistic.trigger_condition = {
        ...(selected.trigger_condition || {}),
        hour: patch.hour,
      };
      delete optimistic.hour;
    }
    // LOT L3 (pessimist MAJEUR-1) : même nid JSONB que l'heure — sans ça
    // l'optimiste écrivait un m.offset_days racine (clé morte) et le <select>
    // (qui lit m.trigger_condition.offset_days) restait figé sur l'ancienne
    // valeur après un 2e changement, en contradiction avec le libellé.
    if (patch.offset_days !== undefined) {
      optimistic.trigger_condition = {
        ...(optimistic.trigger_condition || selected.trigger_condition || {}),
        offset_days: patch.offset_days,
      };
      delete optimistic.offset_days;
    }
    // Système d'avis « option a » : le seuil de ressenti est stocké dans le
    // MÊME nid JSONB (trigger_condition.feedback_threshold) — même patron que
    // hour/offset_days (sinon le <select> qui lit trigger_condition resterait
    // figé après un 2e changement).
    if (patch.feedback_threshold !== undefined) {
      optimistic.trigger_condition = {
        ...(optimistic.trigger_condition || selected.trigger_condition || {}),
        feedback_threshold: patch.feedback_threshold,
      };
      delete optimistic.feedback_threshold;
    }
    setSettingsBusy(true);
    setActionErr(null);
    patchMessage(selected.slug, optimistic); // optimiste (pill liste + éditeur suivent)
    try {
      await saveDirectMessageSettings(selected.slug, patch);
      // LOT L3 : un réglage qui change le déclencheur (offset) doit rafraîchir
      // les conditions MOTEUR affichées (badge « Réglage réel » + libellé
      // Quand) — sinon le libellé resterait périmé jusqu'au reload. Best-effort.
      if (patch.offset_days !== undefined) {
        try {
          const conds = await fetchDirectConditions();
          setEngineConds(conds && conds.ok ? conds : null);
        } catch (eConds) {
          console.error(
            "[direct] refresh conditions après réglage:",
            eConds.message,
          );
        }
      }
    } catch (e) {
      patchMessage(selected.slug, prev); // revert
      setActionErr(e.message || "Réglage échoué");
    } finally {
      setSettingsBusy(false);
    }
  };

  // Filtre de recherche (nom / slug / libellé de déclencheur) ∧ filtre
  // destinataire (lot visuel) — affichage seulement, rien d'autre ne bouge.
  const q = search.trim().toLowerCase();
  const filteredCategories = useMemo(() => {
    // 1.3 go-live (Dimitri) : pas de TITRE ORPHELIN — une catégorie sans
    // aucun message est masquée aussi en vue par défaut (le filtre vide
    // n'existait qu'en mode recherche/rôle). Affichage pur.
    if (!q && roleFilter === "all")
      return categories.filter((c) => (c.messages || []).length > 0);
    return categories
      .map((c) => ({
        ...c,
        messages: (c.messages || []).filter((m) => {
          if (roleFilter !== "all" && recipientRole(m) !== roleFilter) {
            return false;
          }
          if (!q) return true;
          const hay = [m.name, m.slug, m.trigger_condition_label]
            .filter(Boolean)
            .join(" ")
            .toLowerCase();
          return hay.includes(q);
        }),
      }))
      .filter((c) => (c.messages || []).length > 0);
  }, [categories, q, roleFilter]);

  // ── LOT « rangement » — les fragments hors du flux des messages ────────────
  // Décision Dimitri : les morceaux réutilisables ne sont pas des messages du
  // parcours, ils encombrent la liste. On les SORT du flux pour les ranger dans
  // une section repliée en bas — RENDU CLIENT UNIQUEMENT : leur catégorie en
  // base (« Fragments », icône puzzle) n'est pas touchée.
  const messageCategories = useMemo(
    () =>
      filteredCategories.filter((c) => !FRAGMENT_CATEGORY_SLUGS.has(c.slug)),
    [filteredCategories],
  );
  const fragmentCategories = useMemo(
    () => filteredCategories.filter((c) => FRAGMENT_CATEGORY_SLUGS.has(c.slug)),
    [filteredCategories],
  );
  const fragmentCount = fragmentCategories.reduce(
    (n, c) => n + (c.messages || []).length,
    0,
  );
  // Une RECHERCHE ne doit jamais avoir de résultat invisible : si des fragments
  // matchent, la section s'ouvre le temps de la recherche (sans toucher à l'état
  // mémorisé). Le filtre destinataire, lui, ne force rien (les 4 fragments sont
  // voyageur : « Voyageur » rouvrirait la section à chaque fois).
  const fragmentsShown = fragmentsOpen || (q !== "" && fragmentCount > 0);

  // PERF — poignées d'identité FIGÉE vers les actions de la vue. Ces handlers
  // sont redéfinis à chaque rendu et lisent des états qui bougent (`dirty` pour
  // la garde brouillon, `drag`, `selected`…) : passés tels quels en props, leur
  // identité change à chaque frappe et casse toute mémoïsation en aval. On les
  // relaie via une ref tenue à jour à chaque rendu — l'appel part TOUJOURS sur
  // la dernière version, jamais sur une fermeture périmée.
  const latestActionsRef = useRef(null);
  latestActionsRef.current = {
    requestSelect,
    requestCreate,
    handleDrop,
    handleSettings,
  };
  const stableActionsRef = useRef(null);
  if (stableActionsRef.current === null) {
    stableActionsRef.current = {
      select: (slug) => latestActionsRef.current.requestSelect(slug),
      create: (catId, name) =>
        latestActionsRef.current.requestCreate(catId, name),
      drop: (catId, slug) => latestActionsRef.current.handleDrop(catId, slug),
      settings: (patch) => latestActionsRef.current.handleSettings(patch),
    };
  }
  const railApi = stableActionsRef.current;

  // PERF — LE RAIL COMPLET, MÉMOÏSÉ. Rien ici ne dépend du brouillon en cours
  // de saisie : sans cette mémoïsation, taper un caractère dans l'éditeur
  // reconstruisait la trentaine de lignes de la liste (≈ 40 % du temps de
  // rendu). Le JSX est repris TEL QUEL — même balisage, mêmes styles, même
  // ordre : seule la FRÉQUENCE de recalcul change.
  const railContent = useMemo(() => {
    // Bloc de liste d'UNE catégorie — extrait TEL QUEL du rendu précédent pour
    // être réutilisé aux deux endroits (flux des messages / section repliée). Le
    // seul paramètre est `withHead` : dans la section repliée, le bandeau teinté
    // de catégorie est remplacé par l'en-tête de la section (un rangement ne doit
    // pas ressembler à une catégorie de plus). Tout le reste — sélection, DnD
    // intra-catégorie, ligne « Ajouter un message » — est IDENTIQUE.
    const renderCategoryBlock = (cat, { withHead = true } = {}) => (
      <div key={cat.slug} style={{ marginTop: withHead ? 10 : 4 }}>
        {withHead && <CategoryHead cat={cat} />}
        {(cat.messages || []).map((m) => (
          <MessageRow
            key={m.slug}
            m={m}
            selected={m.slug === selectedSlug}
            onSelect={() => railApi.select(m.slug)}
            // M8 : pause EFFECTIVE seulement si le slug est piloté
            // par le moteur (mêmes conditions que la note L2).
            enginePiloted={Boolean(
              engineConds &&
                engineConds.temporels &&
                engineConds.temporels[m.slug],
            )}
            // ── DnD intra-catégorie (3B) ──
            draggable={dndEnabled}
            dragging={!!drag && drag.slug === m.slug}
            dragOver={dragOverSlug === m.slug}
            onDragStart={(e) => {
              if (!dndEnabled) return;
              e.dataTransfer.effectAllowed = "move";
              try {
                e.dataTransfer.setData("text/plain", m.slug);
              } catch (_) {
                /* jsdom/tests : setData absent — sans gravité */
              }
              setDrag({ catId: cat.id, slug: m.slug });
            }}
            onDragOver={(e) => {
              // Cible valide UNIQUEMENT dans la même catégorie.
              if (drag && drag.catId === cat.id && drag.slug !== m.slug) {
                e.preventDefault();
                setDragOverSlug(m.slug);
              }
            }}
            onDragLeave={() => {
              setDragOverSlug((s) => (s === m.slug ? null : s));
            }}
            onDrop={(e) => {
              e.preventDefault();
              railApi.drop(cat.id, m.slug);
            }}
            onDragEnd={() => {
              setDrag(null);
              setDragOverSlug(null);
            }}
          />
        ))}
        <AddMessageLine
          open={addingCat === cat.id}
          onOpen={() => setAddingCat(cat.id)}
          onCancel={() => setAddingCat(null)}
          onCreate={(name) => railApi.create(cat.id, name)}
        />
      </div>
    );

    return (
      <>
        {filteredCategories.length === 0 && (
          <div
            style={{
              color: DS.textMuted,
              fontSize: 13,
              padding: "14px 8px",
            }}
          >
            {search.trim()
              ? `Aucun message ne correspond à « ${search} ».`
              : "Aucun message pour ce destinataire."}
          </div>
        )}
        {messageCategories.map((cat) => renderCategoryBlock(cat))}

        {/* ── Morceaux réutilisables — RANGEMENT, pas une catégorie ─────
            Placée en BAS de la liste, fermée par défaut, visuellement
            distincte des bandeaux de catégorie (pas de teinte, encadré
            pointillé). Les fragments restent pleinement éditables une
            fois ouverte. Choix de RENDU : la catégorie base est
            inchangée. */}
        {fragmentCategories.length > 0 && (
          <div
            data-testid="direct-fragments-section"
            style={{
              marginTop: 20,
              paddingTop: 14,
              borderTop: `1px solid ${DS.border}`,
            }}
          >
            <button
              type="button"
              data-testid="direct-fragments-toggle"
              aria-expanded={fragmentsShown}
              onClick={() => setFragmentsOpen((o) => !o)}
              style={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                gap: 9,
                padding: "8px 10px",
                border: `1px dashed ${DS.borderHover}`,
                background: "transparent",
                borderRadius: DS.radiusControl,
                cursor: "pointer",
                fontFamily: DS.fontBody,
                color: DS.textMuted,
                textAlign: "left",
              }}
            >
              <Puzzle size={15} strokeWidth={2} />
              <span
                style={{
                  fontSize: 10.5,
                  fontWeight: 700,
                  letterSpacing: 1.1,
                  textTransform: "uppercase",
                }}
              >
                Morceaux réutilisables
              </span>
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  background: DS.pageBg,
                  padding: "1px 7px",
                  borderRadius: 9,
                }}
              >
                {fragmentCount}
              </span>
              <ChevronDown
                size={15}
                strokeWidth={2}
                style={{
                  marginLeft: "auto",
                  flexShrink: 0,
                  transform: fragmentsShown ? "rotate(180deg)" : "rotate(0deg)",
                  transition: "transform .15s ease",
                }}
              />
            </button>
            {fragmentsShown && (
              <>
                <div
                  data-testid="direct-fragments-help"
                  style={{
                    fontSize: 11,
                    color: DS.textMuted,
                    lineHeight: 1.45,
                    padding: "9px 10px 3px",
                  }}
                >
                  Ces textes ne partent jamais seuls : ils sont insérés dans un
                  autre e-mail du parcours. Ils se modifient ici comme les
                  autres.
                </div>
                {fragmentCategories.map((cat) =>
                  renderCategoryBlock(cat, { withHead: false }),
                )}
              </>
            )}
          </div>
        )}
      </>
    );
  }, [
    // Entrées RÉELLES du rail — aucune ne bouge pendant la frappe.
    railApi,
    filteredCategories.length,
    search,
    messageCategories,
    fragmentCategories,
    fragmentsShown,
    fragmentCount,
    selectedSlug,
    engineConds,
    dndEnabled,
    drag,
    dragOverSlug,
    addingCat,
  ]);

  return (
    <div style={{ fontFamily: DS.fontBody }}>
      {/* Bandeau titre */}
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          gap: 16,
          flexWrap: "wrap",
          marginBottom: 16,
        }}
      >
        <div style={{ flex: 1, minWidth: 220 }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 7,
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: 1.1,
              textTransform: "uppercase",
              color: DS.successText,
              marginBottom: 6,
            }}
          >
            <span
              style={{
                width: 8,
                height: 8,
                borderRadius: 999,
                background: DS.success,
              }}
            />
            Parcours direct
          </div>
          <h2
            style={{
              fontFamily: DS.fontDisplay,
              fontWeight: 600,
              fontSize: 26,
              letterSpacing: -0.3,
              color: DS.marineDark,
              lineHeight: 1.1,
              margin: 0,
            }}
          >
            Textes du parcours direct
          </h2>
        </div>
        <div style={{ display: "flex", gap: 10, flexShrink: 0 }}>
          {/* 3B : crée dans la catégorie du message sélectionné (ouvre l'input
              « Ajouter un message » de cette catégorie). */}
          <button
            type="button"
            data-testid="direct-msg-new"
            disabled={!selected}
            onClick={() => selected && setAddingCat(selected.category_id)}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              fontFamily: DS.fontBody,
              fontSize: 13,
              fontWeight: 600,
              padding: "10px 15px",
              borderRadius: DS.radiusControl,
              border: "1px solid transparent",
              background: DS.corail,
              color: "#fff",
              cursor: selected ? "pointer" : "not-allowed",
              opacity: selected ? 1 : 0.55,
            }}
          >
            <Plus size={15} strokeWidth={2} />
            Nouveau message
          </button>
        </div>
      </div>

      {loading && (
        <div style={{ color: DS.textMuted, padding: 20 }}>Chargement…</div>
      )}
      {error && (
        <div
          data-testid="direct-msg-error"
          style={{
            background: DS.dangerBg,
            color: DS.dangerText,
            border: `0.5px solid ${DS.danger}`,
            borderRadius: DS.radiusControl,
            padding: "10px 14px",
            marginBottom: 12,
            fontSize: 13,
          }}
        >
          {error}
        </div>
      )}
      {actionErr && (
        <div
          data-testid="direct-msg-action-error"
          style={{
            background: DS.dangerBg,
            color: DS.dangerText,
            border: `0.5px solid ${DS.danger}`,
            borderRadius: DS.radiusControl,
            padding: "10px 14px",
            marginBottom: 12,
            fontSize: 13,
          }}
        >
          {actionErr}
        </div>
      )}

      {!loading && !error && (
        <div
          style={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            gap: 16,
            alignItems: "flex-start",
          }}
        >
          {/* ── Colonne gauche : liste ── */}
          <div
            style={{
              width: isMobile ? "100%" : 360,
              flexShrink: 0,
              background: DS.card,
              border: `0.5px solid ${DS.border}`,
              borderRadius: DS.radiusCard,
              boxShadow: "0 10px 30px -14px rgba(20,51,80,.22)",
              overflow: "hidden",
            }}
          >
            {/* Lot visuel : zone recherche sur fond BLANC (ex-bleuté cielSoft)
                + filtre destinataire segmenté (maquette Plateformes). */}
            <div
              style={{
                padding: 14,
                borderBottom: `1px solid ${DS.border}`,
                background: DS.card,
                display: "flex",
                flexDirection: "column",
                gap: 10,
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 9,
                  background: DS.card,
                  border: `1px solid ${DS.border}`,
                  borderRadius: DS.radiusControl,
                  padding: "8px 11px",
                  color: DS.textMuted,
                }}
              >
                <Search size={15} strokeWidth={2} />
                <input
                  data-testid="direct-msg-search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Rechercher un message…"
                  style={{
                    border: "none",
                    background: "transparent",
                    outline: "none",
                    fontFamily: DS.fontBody,
                    fontSize: 13,
                    flex: 1,
                    color: DS.textPrimary,
                  }}
                />
              </div>
              {/* Filtre destinataire — segmenté façon maquette Plateformes
                  (rail-filter) ; pastilles de rôle aux couleurs Direct
                  (VOY vert / ADM marine). Affichage seulement. */}
              <div
                data-testid="direct-msg-role-filter"
                style={{
                  display: "flex",
                  background: DS.pageBg,
                  borderRadius: 9,
                  padding: 3,
                  gap: 2,
                }}
              >
                {[
                  { key: "all", label: "Tous", dot: null },
                  { key: "VOY", label: "Voyageur", dot: DS.success },
                  { key: "ADM", label: "Admin", dot: DS.marine },
                ].map((f) => {
                  const on = roleFilter === f.key;
                  return (
                    <button
                      key={f.key}
                      type="button"
                      data-testid={`direct-msg-role-filter-${f.key}`}
                      onClick={() => setRoleFilter(f.key)}
                      style={{
                        flex: 1,
                        border: "none",
                        background: on ? DS.card : "transparent",
                        color: on ? DS.marine : DS.textMuted,
                        boxShadow: on ? "0 1px 3px rgba(20,51,80,.14)" : "none",
                        borderRadius: 6,
                        padding: "6px 10px",
                        fontFamily: DS.fontBody,
                        fontSize: 12,
                        fontWeight: 600,
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 5,
                      }}
                    >
                      {f.dot && (
                        <span
                          style={{
                            width: 7,
                            height: 7,
                            borderRadius: "50%",
                            background: f.dot,
                            flexShrink: 0,
                          }}
                        />
                      )}
                      {f.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div
              data-testid="direct-msg-rail"
              style={{
                padding: "6px 8px 12px",
                maxHeight: isMobile ? "none" : "calc(100vh - 150px)",
                overflowY: isMobile ? "visible" : "auto",
              }}
            >
              {railContent}
            </div>
          </div>

          {/* ── Zone droite : éditeur + aperçu ── */}
          <div
            style={{
              flex: 1,
              minWidth: 0,
              width: isMobile ? "100%" : "auto",
              display: "flex",
              flexDirection: isNarrow ? "column" : "row",
              gap: 16,
              alignItems: "flex-start",
            }}
          >
            <div
              style={{
                flex: "1 1 0",
                minWidth: 0,
                width: isNarrow ? "100%" : "auto",
              }}
            >
              {/* Garde-fou brouillon non sauvé (3B) : confirmation avant de
                  perdre des modifs en changeant de message / créant. */}
              {pendingAction && (
                <div
                  data-testid="direct-msg-unsaved-guard"
                  style={{
                    border: `1px solid ${DS.warning}`,
                    background: DS.warningBg,
                    borderRadius: DS.radiusControl,
                    padding: "12px 14px",
                    marginBottom: 12,
                  }}
                >
                  <div
                    style={{
                      fontSize: 13,
                      color: DS.warningText,
                      fontWeight: 600,
                      lineHeight: 1.5,
                      marginBottom: 11,
                    }}
                  >
                    Modifications non enregistrées. Changer quand même ?
                  </div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <button
                      type="button"
                      data-testid="direct-msg-unsaved-continue"
                      onClick={confirmPending}
                      style={{
                        border: `1px solid ${DS.warning}`,
                        background: DS.warning,
                        color: "#fff",
                        fontSize: 13,
                        fontWeight: 600,
                        fontFamily: DS.fontBody,
                        padding: "9px 14px",
                        borderRadius: DS.radiusControl,
                        cursor: "pointer",
                      }}
                    >
                      Continuer (perdre les modifs)
                    </button>
                    <button
                      type="button"
                      data-testid="direct-msg-unsaved-cancel"
                      onClick={cancelPending}
                      style={{
                        border: `1px solid ${DS.border}`,
                        background: DS.card,
                        color: DS.textSecondary,
                        fontSize: 13,
                        fontWeight: 600,
                        fontFamily: DS.fontBody,
                        padding: "9px 14px",
                        borderRadius: DS.radiusControl,
                        cursor: "pointer",
                      }}
                    >
                      Annuler
                    </button>
                  </div>
                </div>
              )}
              {selected ? (
                <EditorCard
                  engineConds={engineConds}
                  m={selected}
                  builder={builder}
                  isManuel={selectedIsManuel}
                  manualVars={manualVars}
                  draft={draft}
                  onChange={setDraft}
                  dirty={dirty}
                  saving={saving}
                  saveErr={saveErr}
                  flash={flash}
                  onSave={handleSave}
                  onReset={handleReset}
                  onDelete={handleDelete}
                  // PERF : relais d'identité stable (cf. `stableActionsRef`) —
                  // permet de mémoïser le bloc « Condition d'envoi », qui ne
                  // dépend pas du texte en cours de saisie. Comportement
                  // identique : l'appel arrive sur le dernier `handleSettings`.
                  onSettings={railApi.settings}
                  settingsBusy={settingsBusy}
                  lang={activeLang}
                  onLangChange={setActiveLang}
                  editorNonce={editorNonce}
                />
              ) : (
                <div style={{ color: DS.textMuted, padding: 20 }}>
                  Sélectionnez un message à gauche.
                </div>
              )}
            </div>

            <div
              style={{
                flex: "1 1 0",
                minWidth: 0,
                width: isNarrow ? "100%" : "auto",
              }}
            >
              {/* QA #1095 — « même écran » : sur écran large, le TITRE, le
                  panneau de scénario ET l'aperçu forment UN SEUL bloc collant.
                  L'aperçu défile DANS ce bloc (hauteur bornée au viewport) :
                  on peut donc cocher une case et voir l'aperçu changer sans
                  jamais perdre les cases de vue, y compris sur un portable
                  ~800 px. En colonne étroite (isNarrow), flux naturel. */}
              <div
                style={
                  isNarrow
                    ? undefined
                    : {
                        position: "sticky",
                        top: 16,
                        maxHeight: "calc(100vh - 32px)",
                        display: "flex",
                        flexDirection: "column",
                        minHeight: 0,
                      }
                }
              >
                <div
                  style={{
                    fontSize: 11,
                    color: DS.marine,
                    marginBottom: 10,
                    fontWeight: 700,
                    letterSpacing: 0.6,
                    textTransform: "uppercase",
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    flex: "0 0 auto",
                  }}
                >
                  <Eye size={13} strokeWidth={2} /> Ce que reçoit le voyageur
                </div>
                {/* B5 — cases à cocher qui simulent l'état d'une réservation et
                    transforment l'aperçu EN DIRECT (même écran que l'éditeur). */}
                {selected && (
                  <div style={{ flex: "0 0 auto" }}>
                    <MemoDirectPreviewScenarioPanel
                      scenario={scenario}
                      onChange={setScenario}
                      openGroups={scenarioOpenGroups}
                      lang={activeLang}
                    />
                  </div>
                )}
                {selected && activeLang === "es" && <EsNotSentYetBanner />}
                {selected && (
                  <div
                    style={
                      isNarrow
                        ? undefined
                        : {
                            flex: "1 1 auto",
                            minHeight: 0,
                            overflowY: "auto",
                            overflowX: "hidden",
                            // marge négative + padding : le défilement interne ne
                            // doit pas rogner l'ombre portée de la carte.
                            margin: "0 -10px",
                            padding: "2px 10px 26px",
                          }
                    }
                  >
                    <PreviewCard
                      subject={previewSubject}
                      body={previewBody}
                      previewVars={previewVars}
                      // Le bloc PARENT est déjà collant : un second `sticky`
                      // imbriqué figerait la carte dans sa propre zone de défilement.
                      sticky={false}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Ligne « Ajouter un message » (3B — ACTIVE) ────────────────────────────────
// Fermée : ligne pointillée cliquable. Ouverte : input inline « Nom du message »
// (Entrée = créer, Échap = annuler). La création passe par le garde-fou brouillon
// du parent (requestCreate).
function AddMessageLine({ open, onOpen, onCancel, onCreate }) {
  const [name, setName] = useState("");
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setName("");
      requestAnimationFrame(() => inputRef.current && inputRef.current.focus());
    }
  }, [open]);

  if (!open) {
    return (
      <div
        data-testid="direct-msg-add-line"
        role="button"
        tabIndex={0}
        onClick={onOpen}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onOpen();
          }
        }}
        style={{
          margin: "6px 6px 0",
          border: `1px dashed ${DS.borderHover}`,
          borderRadius: DS.radiusControl,
          padding: 9,
          textAlign: "center",
          color: DS.marine,
          fontSize: 12.5,
          fontWeight: 600,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 6,
          cursor: "pointer",
        }}
      >
        <Plus size={14} strokeWidth={2} /> Ajouter un message
      </div>
    );
  }

  const submit = () => {
    if (name.trim()) onCreate(name.trim());
  };

  return (
    <div
      data-testid="direct-msg-add-form"
      style={{
        margin: "6px 6px 0",
        border: `1px solid ${DS.ciel}`,
        borderRadius: DS.radiusControl,
        padding: 8,
        display: "flex",
        alignItems: "center",
        gap: 7,
        background: DS.cielSoft,
      }}
    >
      <input
        ref={inputRef}
        data-testid="direct-msg-add-name"
        value={name}
        onChange={(e) => setName(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            submit();
          }
          if (e.key === "Escape") onCancel();
        }}
        placeholder="Nom du message…"
        style={{
          flex: 1,
          minWidth: 0,
          border: `1px solid ${DS.border}`,
          borderRadius: 8,
          padding: "7px 10px",
          fontSize: 12.5,
          fontFamily: DS.fontBody,
          color: DS.textPrimary,
          background: DS.card,
          outline: "none",
        }}
      />
      <button
        type="button"
        data-testid="direct-msg-add-submit"
        disabled={!name.trim()}
        onClick={submit}
        style={{
          border: "1px solid transparent",
          background: DS.corail,
          color: "#fff",
          borderRadius: 8,
          padding: "7px 11px",
          fontSize: 12.5,
          fontWeight: 600,
          fontFamily: DS.fontBody,
          cursor: name.trim() ? "pointer" : "not-allowed",
          opacity: name.trim() ? 1 : 0.5,
        }}
      >
        Créer
      </button>
      <button
        type="button"
        onClick={onCancel}
        title="Annuler"
        style={{
          border: `1px solid ${DS.border}`,
          background: DS.card,
          color: DS.textMuted,
          borderRadius: 8,
          padding: "7px 9px",
          fontSize: 12.5,
          fontFamily: DS.fontBody,
          cursor: "pointer",
        }}
      >
        ✕
      </button>
    </div>
  );
}

// ── En-tête de catégorie ──────────────────────────────────────────────────────
// Icône par catégorie (mapping CATICON de la maquette Direct, équivalents
// lucide) — rendue dans un carré arrondi façon maquette (.ci 28×28).
const CATEGORY_ICONS = {
  demande: MailPlus,
  validation: CheckCheck,
  signature: Signature,
  paiement: Banknote,
  confirmation: CheckCircle2,
  rappels: BellRing,
  depart: PlaneTakeoff,
  suivi_admin: ShieldHalf,
  // CHANTIER 0.1 — fragments éditables (corps injectés dans les emails porteurs).
  fragments: Puzzle,
};

// F.1 — teinte d'accent PAR CATÉGORIE : pattern copié de FlowSection
// (Plateformes, src/tabs/MessagesPanel.jsx) — bandeau sur tint.bg + icône
// tint.fg dans un carré blanc. UNIQUEMENT des tokens DS existants ; mapping
// aligné sur Plateformes quand l'équivalent existe (cohérence F.1) :
//   demande      ≡ « RÉSERVATION REÇUE » (catCiel — entrée du flux)
//   validation   → corail = actions / moments clés (l'acceptation FBZ)
//   signature    → catRose (étape contractuelle, 5e tint distincte)
//   paiement     → success (sémantique DS « payé, caution »)
//   confirmation → success (confirmé = « fait » — couloir vert assumé avec
//                  paiement, précédent Plateformes : ardoise ×2 adjacents)
//   rappels      ≡ « AVANT ARRIVÉE » (catIndigo — rappels caution J-2/J-1/J0)
//   depart       ≡ « APRÈS DÉPART » (catArdoise)
//   suivi_admin  ≡ tag « Admin » Plateformes (cielSoft / marine)
//   fragments    ≡ « AUTRES SENDS » (catArdoise — même icône Puzzle)
const CATEGORY_TINTS = {
  demande: { bg: DS.catCielBg, fg: DS.catCielIco },
  validation: { bg: DS.catCorailBg, fg: DS.catCorailIco },
  signature: { bg: DS.catRoseBg, fg: DS.catRoseIco },
  paiement: { bg: DS.successBg, fg: DS.successText },
  confirmation: { bg: DS.successBg, fg: DS.successText },
  rappels: { bg: DS.catIndigoBg, fg: DS.catIndigoIco },
  depart: { bg: DS.catArdoiseBg, fg: DS.catArdoiseIco },
  suivi_admin: { bg: DS.cielSoft, fg: DS.marine },
  fragments: { bg: DS.catArdoiseBg, fg: DS.catArdoiseIco },
};
// Catégorie inconnue → visuel historique (cielSoft/marine), jamais de couleur
// inventée.
const DEFAULT_TINT = { bg: DS.cielSoft, fg: DS.marine };

function CategoryHead({ cat }) {
  const CatIcon = CATEGORY_ICONS[cat.slug] || Folder;
  const tint = CATEGORY_TINTS[cat.slug] || DEFAULT_TINT;
  return (
    // Lot visuel : bandeau de catégorie sur fond teinté (F.1 : teinte propre
    // à la catégorie, pattern FlowSection de Plateformes) + icône colorée
    // dans un carré blanc arrondi (façon maquette).
    <div
      data-testid={`direct-cat-head-${cat.slug}`}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "6px 10px 6px 6px",
        background: tint.bg,
        borderRadius: DS.radiusControl,
        marginBottom: 4,
      }}
    >
      <span
        style={{
          width: 28,
          height: 28,
          borderRadius: 8,
          background: DS.card,
          color: tint.fg,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <CatIcon size={16} strokeWidth={2} />
      </span>
      <span
        style={{
          fontSize: 10.5,
          fontWeight: 700,
          letterSpacing: 1.1,
          textTransform: "uppercase",
          color: DS.marineDeep,
        }}
      >
        {cat.label}
      </span>
      <span
        style={{
          fontSize: 11,
          color: DS.marineDeep,
          fontWeight: 700,
          background: DS.card,
          padding: "1px 7px",
          borderRadius: 9,
        }}
      >
        {(cat.messages || []).length}
      </span>
      <span
        style={{ marginLeft: "auto", color: DS.borderHover, display: "flex" }}
        title="Glissez un message pour changer l'ordre (videz d'abord la recherche et les filtres)"
      >
        <GripVertical size={15} strokeWidth={2} />
      </span>
    </div>
  );
}

// ── Ligne de message (carte de liste) ─────────────────────────────────────────
function MessageRow({
  m,
  selected,
  onSelect,
  // M8 : true si le slug est réellement piloté par le moteur d'envoi
  // (engineConds.temporels) — la pastille Brouillon y vaut PAUSE effective (L2).
  enginePiloted,
  // ── DnD intra-catégorie (3B) — câblé par le parent ──
  draggable,
  dragging,
  dragOver,
  onDragStart,
  onDragOver,
  onDragLeave,
  onDrop,
  onDragEnd,
}) {
  const role = recipientRole(m);
  const isVoy = role === "VOY";
  const isActif = m.status === "actif";
  const selBorder = isVoy ? DS.success : DS.marine;
  return (
    <div
      data-testid={`direct-msg-item-${m.slug}`}
      className="fbz-press"
      onClick={onSelect}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onSelect();
        }
      }}
      draggable={!!draggable}
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      onDragEnd={onDragEnd}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "10px 9px",
        borderRadius: DS.radiusControl,
        cursor: "pointer",
        // Lot visuel : sélection = CONTOUR complet coloré selon le rôle
        // (VOY vert / ADM marine) + surélévation, fond BLANC/neutre (décision
        // Dimitri : jamais de fond de couleur) — remplace le fond plein +
        // liseré gauche.
        background: DS.card,
        border: `1.5px solid ${selected ? selBorder : "transparent"}`,
        boxShadow: selected ? "0 7px 18px -8px rgba(20,51,80,.35)" : "none",
        marginBottom: 3,
        // M8 : un message en Brouillon s'atténue légèrement dans la liste
        // (la pause / l'inactivité se voit d'un coup d'œil) — sauf quand il
        // est sélectionné (lisibilité pendant l'édition). Actifs : inchangés.
        // ── retours visuels DnD ──
        opacity: dragging ? 0.45 : !isActif && !selected ? 0.75 : 1,
        outline: dragOver ? `2px solid ${DS.ciel}` : "none",
        outlineOffset: dragOver ? -2 : 0,
      }}
    >
      <span
        style={{
          color: DS.borderHover,
          display: "flex",
          flexShrink: 0,
          cursor: draggable ? "grab" : "default",
        }}
        title={
          draggable
            ? "Glisser pour réordonner (dans sa catégorie)"
            : "Réordonnancement indisponible pendant une recherche"
        }
      >
        <GripVertical size={16} strokeWidth={2} />
      </span>
      <RoleBadge role={role} compact />
      {/* Lot visuel : le sous-titre explicatif gris (condition/slug) est retiré
          de la liste (décision Dimitri) — titre + badges seuls ; la condition
          reste lisible dans l'éditeur (bloc « Condition d'envoi »). */}
      <span style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontFamily: DS.fontDisplay,
            fontWeight: 600,
            fontSize: 13.5,
            color: DS.marineDark,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {m.name}
        </div>
      </span>
      <span
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          flexShrink: 0,
        }}
      >
        <span
          style={{ display: "flex", gap: 4, color: DS.textMuted }}
          title="Email"
        >
          <Mail size={14} strokeWidth={2} />
        </span>
        <StatusPill actif={isActif} paused={!isActif && !!enginePiloted} />
        <span
          style={{ color: DS.borderHover, display: "flex", opacity: 0.6 }}
        >
          <MoreVertical size={16} strokeWidth={2} />
        </span>
      </span>
    </div>
  );
}

function RoleBadge({ role, compact }) {
  const isVoy = role === "VOY";
  const Icon = isVoy ? User : ShieldCheck;
  return (
    <span
      title={
        isVoy
          ? "Destinataire : voyageur"
          : "Destinataire : équipe FBZ (interne)"
      }
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        flexShrink: 0,
        fontSize: compact ? 9.5 : 11,
        fontWeight: 700,
        letterSpacing: 0.5,
        padding: compact ? "3px 6px" : "4px 9px",
        borderRadius: 5,
        lineHeight: 1,
        background: isVoy ? DS.successBg : DS.cielSoft,
        color: isVoy ? DS.successText : DS.marineDeep,
      }}
    >
      <Icon size={compact ? 11 : 13} strokeWidth={2.2} />
      {isVoy ? "VOYAGEUR" : "ÉQUIPE"}
    </span>
  );
}

// M8 « visibilité pause » : le MOT « Brouillon » est la sémantique produit L2
// établie (on ne le renomme PAS) mais la pill passe en AMBRE (warning) pour
// être lisible d'un coup d'œil — l'ancien gris pageBg se fondait dans la carte.
// Le préfixe ⏸ + tooltip « En pause » ne s'affichent QUE si la pause est
// EFFECTIVE (slug temporel piloté par le moteur, L2) : pour un slug sys
// (transactionnel) la pastille ne suspend rien — pas de fausse promesse.
function StatusPill({ actif, paused }) {
  return (
    <span
      title={
        paused
          ? "En pause : cet envoi automatique est suspendu — il ne partira pas au passage quotidien."
          : undefined
      }
      style={{
        fontSize: 9,
        fontWeight: 700,
        letterSpacing: 0.3,
        padding: "3px 7px",
        borderRadius: 5,
        textTransform: "uppercase",
        whiteSpace: "nowrap",
        background: actif ? DS.successBg : DS.warningBg,
        color: actif ? DS.successText : DS.warningText,
      }}
    >
      {actif ? "Actif" : paused ? "⏸ Brouillon" : "Brouillon"}
    </span>
  );
}

// ── Colonne centre : éditeur (contenu ÉDITABLE, sous-lot 3A) ──────────────────
function EditorCard({
  m,
  builder,
  engineConds,
  isManuel,
  manualVars,
  draft,
  onChange,
  dirty,
  saving,
  saveErr,
  flash,
  onSave,
  onReset,
  onDelete,
  onSettings,
  settingsBusy,
  lang,
  onLangChange,
  editorNonce,
}) {
  const role = recipientRole(m);
  const [confirmReset, setConfirmReset] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    setConfirmReset(false);
    setConfirmDelete(false);
    setActiveField("body"); // nouveau message → cible par défaut = Corps
  }, [m.slug]);

  // Groupes de variables du picker « + Info voyageur » (SegmentEditor) : celles
  // du gabarit — ou, pour un message MANUEL (3B, pas de vars per-slug), le
  // catalogue complet (même référentiel que la validation serveur).
  const varGroups = useMemo(
    () => buildVarGroups(isManuel ? manualVars : builder && builder.vars),
    [isManuel, manualVars, builder],
  );

  // M12+ : UN SEUL bouton « + Info voyageur » partagé (au-dessus des deux
  // champs) → insère dans le CHAMP ACTIF (le dernier cliqué). Chaque
  // SegmentEditor peuple sa ref-API (insertVariable au caret mémorisé) et
  // notifie `activeField` sur focus. Défaut = Corps : les variables vont dans le
  // corps ~toujours et le bouton reste utilisable sans clic préalable (reco vs
  // bouton grisé). Repli si le champ actif est en lecture seule → Corps.
  const subjectApiRef = useRef({});
  const bodyApiRef = useRef({});
  const [activeField, setActiveField] = useState("body");
  // M2 : si les DEUX champs sont en lecture seule (contenu non round-trippable),
  // le bouton partagé n'a nulle part où insérer → on le grise (pas de no-op muet).
  const [subjectRO, setSubjectRO] = useState(false);
  const [bodyRO, setBodyRO] = useState(false);
  function insertVarInActiveField(key) {
    const active =
      activeField === "subject" ? subjectApiRef.current : bodyApiRef.current;
    if (active && active.insertVariable && !active.readOnly)
      return active.insertVariable(key);
    const body = bodyApiRef.current;
    if (body && body.insertVariable && !body.readOnly) body.insertVariable(key);
  }

  const isSys = m.trigger_kind === "sys";
  const isEdited = !!m.is_edited;
  // Post-bascule B1 : le contenu édité pilote les envois réels — mais UNIQUEMENT
  // pour les messages effectivement adressés par un sender. Les manuels et les
  // slugs de NOT_WIRED_SLUGS ne sont branchés à aucun envoi : le flash de save
  // doit le dire (règle « texte d'aide = état actuel », finding pessimist #934).
  const isWired = !isManuel && !NOT_WIRED_SLUGS.has(m.slug);

  // ── Lot L : champs de la LANGUE ACTIVE (FR par défaut ; EN sur les
  // bilingues via le LangToggle). Le brouillon porte les deux langues — un
  // seul Enregistrer écrit tout.
  const isBilingue = !!m.bilingue && !isManuel;
  const isEn = isBilingue && lang === "en";
  // B4-ext — 3e langue ES (l'éditeur Plateformes a FR/EN/ES) : mêmes colonnes que
  // la base (subject_es/body_es), désormais ENREGISTRÉE par le contrat serveur
  // (mêmes règles que l'EN : bilingues uniquement, tout-ou-rien). Le SERVICE du
  // texte ES à l'envoi reste à brancher — note honnête ci-dessous.
  const isEs = isBilingue && lang === "es";
  const subjectField = isEn ? "subject_en" : isEs ? "subject_es" : "subject";
  const bodyField = isEn ? "body_en" : isEs ? "body_es" : "body";
  const langSuffix = isEn ? " (EN)" : isEs ? " (ES)" : "";

  // Objet ET corps FR requis (le contenu remplace entièrement le défaut — cf.
  // résolveur all-or-nothing). Pour un manuel : le nom aussi (renommable).
  // Lot L : l'EN doit être COHÉRENT — objet ET corps EN, ou aucun des deux
  // (contrat serveur tout-ou-rien). Save actif si modifié + complet + cohérent.
  const enPartial =
    isBilingue && !!draft.subject_en.trim() !== !!draft.body_en.trim();
  // B4-ext : l'ES est persisté → MÊME garde tout-ou-rien que l'EN (le serveur
  // refuse un ES partiel avec `es_incomplet` ; on l'annonce avant l'aller-retour).
  const esPartial =
    isBilingue && !!draft.subject_es.trim() !== !!draft.body_es.trim();
  const complete =
    !!draft.subject.trim() &&
    !!draft.body.trim() &&
    (!isManuel || !!draft.name.trim());
  const canSave = dirty && complete && !enPartial && !esPartial && !saving;
  const canReset = (isEdited || dirty) && !saving;

  return (
    <div
      data-testid="direct-msg-editor"
      style={{
        background: DS.card,
        border: `0.5px solid ${DS.border}`,
        borderRadius: DS.radiusCard,
        boxShadow: "0 8px 24px -14px rgba(20,51,80,.2)",
        overflow: "hidden",
      }}
    >
      {/* En-tête */}
      <div
        style={{
          padding: "16px 18px 14px",
          borderBottom: `1px solid ${DS.border}`,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <RoleBadge role={role} />
          {isManuel ? (
            // Message manuel (3B) : nom RENOMMABLE (enregistré avec le contenu).
            <input
              data-testid="direct-msg-name"
              value={draft.name}
              onChange={(e) => onChange({ ...draft, name: e.target.value })}
              placeholder="Nom du message…"
              style={{
                fontFamily: DS.fontDisplay,
                fontWeight: 600,
                fontSize: 19,
                letterSpacing: -0.2,
                color: DS.marineDark,
                border: `1px dashed ${DS.border}`,
                borderRadius: 8,
                padding: "4px 9px",
                background: "transparent",
                outline: "none",
                flex: 1,
                minWidth: 0,
              }}
            />
          ) : (
            <span
              style={{
                fontFamily: DS.fontDisplay,
                fontWeight: 600,
                fontSize: 19,
                letterSpacing: -0.2,
                color: DS.marineDark,
              }}
            >
              {m.name}
            </span>
          )}
          {isManuel && <ManuelBadge />}
          {isEdited && !isManuel && <EditedBadge />}
          {/* Lot visuel : statut Actif/Brouillon = pastille CLIQUABLE dans
              l'en-tête (l'ex-section « Statut » du lot C est supprimée — même
              onSettings optimiste+revert, fonctionnel intouché). La note
              honnête sys « il part toujours » vit dans le tooltip. */}
          <button
            type="button"
            data-testid="direct-msg-status-toggle"
            disabled={settingsBusy}
            onClick={() =>
              onSettings({
                status: m.status === "actif" ? "brouillon" : "actif",
              })
            }
            title={
              isSys
                ? `Message automatique${MONEY_SLUGS.has(m.slug) ? " lié au paiement" : ""} : il part toujours ; ce statut est indicatif. Cliquez pour changer.`
                : "Cliquer pour basculer Actif/Brouillon"
            }
            style={{
              marginLeft: "auto",
              flexShrink: 0,
              border: "1px solid transparent",
              cursor: settingsBusy ? "wait" : "pointer",
              fontFamily: DS.fontBody,
              fontSize: 9.5,
              fontWeight: 700,
              letterSpacing: 0.3,
              padding: "4px 9px",
              borderRadius: 5,
              textTransform: "uppercase",
              whiteSpace: "nowrap",
              background: m.status === "actif" ? DS.successBg : DS.pageBg,
              color: m.status === "actif" ? DS.successText : DS.textSecondary,
              opacity: settingsBusy ? 0.6 : 1,
            }}
          >
            {m.status === "actif" ? "Actif" : "Brouillon"}
          </button>
        </div>
      </div>

      <div style={{ padding: "18px 18px 20px" }}>
        {/* Bloc Condition d'envoi */}
        <FieldLabel
          icon={Route}
          label="Condition d'envoi"
          hint="— affichée et modifiable"
        />
        {isManuel ? (
          // Message manuel : honnête sur l'état réel — non branché à un envoi.
          <div
            data-testid="direct-msg-manuel-trigger"
            style={{
              border: `1px dashed ${DS.border}`,
              borderRadius: DS.radiusControl,
              padding: "12px 14px",
              fontSize: 13,
              color: DS.textMuted,
              lineHeight: 1.5,
              background: DS.pageBg,
            }}
          >
            Message créé à la main : il n'est relié à aucun envoi automatique.
            Rien ne part.
          </div>
        ) : (
          <TriggerBlock
            m={m}
            isSys={isSys}
            onSettings={onSettings}
            settingsBusy={settingsBusy}
            engineConds={engineConds}
          />
        )}

        {/* Le statut Actif/Brouillon vit désormais dans l'en-tête (pastille
            cliquable à côté du titre, note sys en tooltip) — lot visuel. */}

        {/* Canaux (lot C : email verrouillé sur les sys). Le canal SMS a désormais
            son propre onglet — il n'est plus présenté ici (retrait « SMS à venir »
            2026-07-22, ADR-266 : Direct SÉPARÉ, l'envoi SMS réel J0 est intact). */}
        <div style={{ marginTop: 16 }}>
          <FieldLabel icon={Radio} label="Canaux d'envoi" />
          <div style={{ display: "flex", gap: 10 }}>
            <ChannelBox
              label="Email"
              icon={Mail}
              active={(m.canal || ["email"]).includes("email")}
              locked={isSys}
              lockedTitle="Message système/paiement : le canal email ne peut pas lui être retiré"
              testid="direct-msg-canal-email"
              disabled={settingsBusy}
              onToggle={() => {
                const cur = Array.isArray(m.canal) ? m.canal : ["email"];
                const next = cur.includes("email")
                  ? cur.filter((c) => c !== "email")
                  : ["email", ...cur];
                onSettings({ canal: next });
              }}
            />
          </div>
        </div>

        {/* Zone message */}
        <div style={{ marginTop: 16 }}>
          <div
            style={{
              border: `1px solid ${DS.border}`,
              borderRadius: 12,
              padding: 15,
              background: DS.pageBg,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 10,
                marginBottom: 12,
                flexWrap: "wrap",
              }}
            >
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: 0.6,
                  color: DS.marine,
                  display: "flex",
                  alignItems: "center",
                  gap: 7,
                }}
              >
                <Mail size={14} strokeWidth={2} /> Le message
              </span>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                {/* M12+ : bouton « + Info voyageur » UNIQUE partagé (objet +
                    corps) — insère dans le champ actif (dernier cliqué). */}
                <VarPickerButton
                  varGroups={varGroups}
                  labelFor={directVarLabel}
                  onPick={insertVarInActiveField}
                  disabled={subjectRO && bodyRO}
                  title={
                    subjectRO && bodyRO
                      ? "Champs en lecture seule — insertion indisponible"
                      : undefined
                  }
                />
                {/* Lot L : bascule FR/EN sur les bilingues uniquement. */}
                {isBilingue && (
                  <LangToggle lang={lang} onChange={onLangChange} />
                )}
              </div>
            </div>

            {/* M12 + B4 — Objet + Corps : SegmentEditor PARTAGÉ (pastilles de
                variables + picker « + Info voyageur » par champ, comme
                Plateformes). La string du brouillon reste la source de vérité
                ({tokens}) — les pastilles sont une couche d'affichage/édition,
                re-sérialisation byte-identique. B4 : le CORPS a désormais
                allowBlocks (blocs {#if} Direct, cf. plus bas) ; l'OBJET reste
                sans blocs (mono-ligne — un \n casse un header mail). Key = slug
                + champ (langue) + nonce : remontage au changement de
                message/langue et sur mutation programmatique du brouillon. */}

            {/* Objet (langue active) */}
            <SubLabel>Objet{langSuffix}</SubLabel>
            <div data-testid="direct-msg-subject" style={{ marginBottom: 14 }}>
              <SegmentEditor
                key={`subject:${m.slug}:${subjectField}:${editorNonce}`}
                variant="subject"
                value={draft[subjectField]}
                onChange={(v) => onChange({ ...draft, [subjectField]: v })}
                varGroups={varGroups}
                allowBlocks={false}
                labelFor={directVarLabel}
                showVarPicker={false}
                apiRef={subjectApiRef}
                onFieldFocus={() => setActiveField("subject")}
                onReadOnly={setSubjectRO}
              />
            </div>

            {/* Corps (langue active) */}
            <SubLabel>Corps{langSuffix}</SubLabel>
            <div data-testid="direct-msg-body">
              {/* B4 — allowBlocks=true : le moteur riche Direct
                  (lib/direct-rich-render.js, B1) évalue désormais les blocs
                  {#if …}…{/if}. Le menu « + Paragraphe conditionnel » propose
                  les conditions DIRECT (catalogue B2 : caution_empreinte,
                  formulaire_manquant, remboursement_*…) via blockGroups, et les
                  encarts portent le bandeau « SEULEMENT SI … » via blockMetaFor.
                  La validation serveur unknownTokens (par-slug + GLOBAL_VALID_VARS)
                  refuse toute condition hors référentiel. B4-ext : le CUMUL
                  ({#if A ET B} / {#if A OU B}) est round-trippé et éditable —
                  puces par branche + sélecteur ET/OU dans l'en-tête de l'encart
                  (le moteur riche l'évaluait déjà). `allowCumul` est OPT-IN et
                  posé ICI SEULEMENT : l'onglet Plateformes reste strictement
                  inchangé (décision de non-régression B4-ext). */}
              <SegmentEditor
                key={`body:${m.slug}:${bodyField}:${editorNonce}`}
                variant="body"
                value={draft[bodyField]}
                onChange={(v) => onChange({ ...draft, [bodyField]: v })}
                varGroups={varGroups}
                allowBlocks
                allowCumul
                blockGroups={DIRECT_CONDITION_BLOCK_GROUPS}
                blockMetaFor={directIfEncartMeta}
                labelFor={directVarLabel}
                showVarPicker={false}
                apiRef={bodyApiRef}
                onFieldFocus={() => setActiveField("body")}
                onReadOnly={setBodyRO}
              />
            </div>
            {/* B4-ext — version ES : DÉSORMAIS ENREGISTRÉE (colonnes
                subject_es/body_es, contrat serveur tout-ou-rien comme l'EN).
                Note honnête « état réel » (règle permanente Dimitri) : le texte
                est bien stocké, mais l'e-mail espagnol part encore d'une
                traduction automatique du français (lib/email-i18n-sender.js) —
                la façade d'envoi ne lit pas encore les colonnes ES. */}
            {isEs && (
              <div
                data-testid="direct-msg-es-note"
                style={{
                  marginTop: 10,
                  padding: "8px 11px",
                  background: DS.warningBg,
                  border: `0.5px solid ${DS.warning}`,
                  borderRadius: 8,
                  fontSize: 11.5,
                  color: DS.warningText,
                  lineHeight: 1.5,
                }}
              >
                Version espagnole <strong>enregistrée</strong> avec le message.
                Aujourd'hui, l'e-mail espagnol est encore traduit
                automatiquement depuis le français au moment de l'envoi : le
                texte saisi ici est conservé, il sera utilisé tel quel quand
                l'envoi en espagnol sera en place.
              </div>
            )}
            {/* B4-ext : ES partiel → Enregistrer bloqué, explication claire
                (miroir exact du garde EN). */}
            {esPartial && (
              <div
                data-testid="direct-msg-es-partial"
                style={{
                  marginTop: 9,
                  border: `1px solid ${DS.warning}`,
                  background: DS.warningBg,
                  color: DS.warningText,
                  borderRadius: 8,
                  padding: "8px 11px",
                  fontSize: 12,
                  fontWeight: 600,
                  lineHeight: 1.45,
                }}
              >
                La version espagnole doit être complète : objet ET corps — ou
                vide les deux champs ES pour garder l'espagnol par défaut.
              </div>
            )}
            {/* Lot visuel : le texte d'aide « variables entre accolades » est
                retiré (décision Dimitri) — le menu + Info voyageur reste ; la
                règle EN tout-ou-rien est expliquée par le garde enPartial. */}
            {/* Lot L : EN partiel → Enregistrer bloqué, explication claire. */}
            {enPartial && (
              <div
                data-testid="direct-msg-en-partial"
                style={{
                  marginTop: 9,
                  border: `1px solid ${DS.warning}`,
                  background: DS.warningBg,
                  color: DS.warningText,
                  borderRadius: 8,
                  padding: "8px 11px",
                  fontSize: 12,
                  fontWeight: 600,
                  lineHeight: 1.45,
                }}
              >
                La version anglaise doit être complète : objet ET corps — ou
                vide les deux champs EN pour garder l'anglais par défaut.
              </div>
            )}
          </div>
        </div>

        {/* Référence : texte par défaut ACTUEL (lecture seule, jamais enregistré),
            dans la LANGUE ACTIVE (lot L). Un message MANUEL n'a pas de défaut
            builder → pas de référence. */}
        {!isManuel && (
          <DefaultReference
            def={(builder && builder.default) || EMPTY_BUILDER_DEFAULT}
            lang={isBilingue ? lang : "fr"}
          />
        )}

        {/* Erreur / confirmation de sauvegarde */}
        {saveErr && (
          <div
            data-testid="direct-msg-save-error"
            style={{
              background: DS.dangerBg,
              color: DS.dangerText,
              border: `0.5px solid ${DS.danger}`,
              borderRadius: DS.radiusControl,
              padding: "9px 12px",
              marginTop: 14,
              fontSize: 12.5,
              lineHeight: 1.45,
            }}
          >
            {saveErr}
          </div>
        )}
        {flash && !saveErr && (
          <div
            data-testid="direct-msg-save-ok"
            style={{
              background: DS.successBg,
              color: DS.successText,
              border: `0.5px solid ${DS.success}`,
              borderRadius: DS.radiusControl,
              padding: "9px 12px",
              marginTop: 14,
              fontSize: 12.5,
            }}
          >
            {flash === "saved"
              ? isWired
                ? "Enregistré. Ce texte s'applique aux envois réels du parcours Direct."
                : "Enregistré. Ce message n'est branché à aucun envoi pour l'instant — rien ne part."
              : "Revenu au texte par défaut."}
          </div>
        )}

        {/* Actions */}
        <div
          style={{ display: "flex", gap: 10, marginTop: 16, flexWrap: "wrap" }}
        >
          <button
            type="button"
            data-testid="direct-msg-save"
            disabled={!canSave}
            onClick={onSave}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              fontFamily: DS.fontBody,
              fontSize: 13,
              fontWeight: 600,
              padding: "10px 15px",
              borderRadius: DS.radiusControl,
              border: "1px solid transparent",
              background: DS.corail,
              color: "#fff",
              cursor: canSave ? "pointer" : "not-allowed",
              opacity: canSave ? 1 : 0.5,
            }}
          >
            <Save size={15} strokeWidth={2} />
            {saving ? "Enregistrement…" : "Enregistrer"}
          </button>

          {/* Message MANUEL (3B) : pas de « Revenir au défaut » (aucun défaut),
              mais « Supprimer » avec confirmation explicite. */}
          {isManuel &&
            (confirmDelete ? (
              <div
                data-testid="direct-msg-delete-confirmbox"
                style={{
                  flexBasis: "100%",
                  border: `1px solid ${DS.danger}`,
                  background: DS.dangerBg,
                  borderRadius: DS.radiusControl,
                  padding: "12px 14px",
                }}
              >
                <div
                  style={{
                    fontSize: 13,
                    color: DS.dangerText,
                    fontWeight: 600,
                    display: "flex",
                    gap: 8,
                    alignItems: "flex-start",
                    lineHeight: 1.5,
                    marginBottom: 11,
                  }}
                >
                  <Trash2
                    size={15}
                    strokeWidth={2}
                    style={{ flexShrink: 0, marginTop: 1 }}
                  />
                  Ceci supprime définitivement ce message. Continuer ?
                </div>
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <button
                    type="button"
                    data-testid="direct-msg-delete-confirm"
                    disabled={saving}
                    onClick={() => {
                      setConfirmDelete(false);
                      onDelete();
                    }}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 7,
                      fontFamily: DS.fontBody,
                      fontSize: 13,
                      fontWeight: 600,
                      padding: "9px 14px",
                      borderRadius: DS.radiusControl,
                      border: `1px solid ${DS.danger}`,
                      background: DS.danger,
                      color: "#fff",
                      cursor: "pointer",
                    }}
                  >
                    Oui, supprimer
                  </button>
                  <button
                    type="button"
                    data-testid="direct-msg-delete-cancel"
                    onClick={() => setConfirmDelete(false)}
                    style={{
                      border: `1px solid ${DS.border}`,
                      background: DS.card,
                      color: DS.textSecondary,
                      fontSize: 13,
                      fontWeight: 600,
                      fontFamily: DS.fontBody,
                      padding: "9px 14px",
                      borderRadius: DS.radiusControl,
                      cursor: "pointer",
                    }}
                  >
                    Annuler
                  </button>
                </div>
              </div>
            ) : (
              <button
                type="button"
                data-testid="direct-msg-delete"
                disabled={saving}
                onClick={() => setConfirmDelete(true)}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                  fontFamily: DS.fontBody,
                  fontSize: 13,
                  fontWeight: 600,
                  padding: "10px 15px",
                  borderRadius: DS.radiusControl,
                  border: `1px solid ${DS.danger}`,
                  background: DS.card,
                  color: DS.dangerText,
                  cursor: saving ? "not-allowed" : "pointer",
                  opacity: saving ? 0.5 : 1,
                }}
              >
                <Trash2 size={15} strokeWidth={2} /> Supprimer ce message
              </button>
            ))}

          {!isManuel && confirmReset ? (
            <div
              data-testid="direct-msg-reset-confirmbox"
              style={{
                flexBasis: "100%",
                border: `1px solid ${DS.danger}`,
                background: DS.dangerBg,
                borderRadius: DS.radiusControl,
                padding: "12px 14px",
              }}
            >
              <div
                style={{
                  fontSize: 13,
                  color: DS.dangerText,
                  fontWeight: 600,
                  display: "flex",
                  gap: 8,
                  alignItems: "flex-start",
                  lineHeight: 1.5,
                  marginBottom: 11,
                }}
              >
                <RotateCcw
                  size={15}
                  strokeWidth={2}
                  style={{ flexShrink: 0, marginTop: 1 }}
                />
                Ceci supprime votre texte personnalisé et revient au texte
                d'origine. Continuer ?
              </div>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <button
                  type="button"
                  data-testid="direct-msg-reset-confirm"
                  disabled={saving}
                  onClick={() => {
                    setConfirmReset(false);
                    onReset();
                  }}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 7,
                    fontFamily: DS.fontBody,
                    fontSize: 13,
                    fontWeight: 600,
                    padding: "9px 14px",
                    borderRadius: DS.radiusControl,
                    border: `1px solid ${DS.danger}`,
                    background: DS.danger,
                    color: "#fff",
                    cursor: "pointer",
                  }}
                >
                  Oui, revenir au défaut
                </button>
                <button
                  type="button"
                  data-testid="direct-msg-reset-cancel"
                  onClick={() => setConfirmReset(false)}
                  style={{
                    border: `1px solid ${DS.border}`,
                    background: DS.card,
                    color: DS.textSecondary,
                    fontSize: 13,
                    fontWeight: 600,
                    fontFamily: DS.fontBody,
                    padding: "9px 14px",
                    borderRadius: DS.radiusControl,
                    cursor: "pointer",
                  }}
                >
                  Annuler
                </button>
              </div>
            </div>
          ) : !isManuel ? (
            <button
              type="button"
              data-testid="direct-msg-reset"
              disabled={!canReset}
              onClick={() => setConfirmReset(true)}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                fontFamily: DS.fontBody,
                fontSize: 13,
                fontWeight: 600,
                padding: "10px 15px",
                borderRadius: DS.radiusControl,
                border: `1px solid ${DS.border}`,
                background: DS.card,
                color: DS.marine,
                cursor: canReset ? "pointer" : "not-allowed",
                opacity: canReset ? 1 : 0.5,
              }}
            >
              <RotateCcw size={15} strokeWidth={2} /> Revenir au défaut
            </button>
          ) : null}
        </div>

        {/* Pas de bandeau d'état permanent (décision Dimitri #934 : gain de
            place). L'état réel est dit au moment du save par le flash ci-dessus
            (gaté isWired) ; les non-branchés ont leurs notes dédiées. */}
      </div>
    </div>
  );
}

// ── Référence : texte par défaut actuel (lecture seule) ───────────────────────
// Repliable. Montre le texte codé (builder) rendu avec des valeurs d'EXEMPLE —
// c'est ce qui part au voyageur tant que tu n'as pas enregistré ta version. NE
// PAS copier-coller tel quel (il contient des exemples type « Camille » / un lien
// d'exemple) : recompose avec les vraies variables du menu.
// PERF : mémoïsé — le texte par défaut affiché en référence ne dépend PAS du
// brouillon en cours de saisie (`def` = objet du builder, identité stable).
const DefaultReference = memo(function DefaultReference({ def, lang = "fr" }) {
  const [open, setOpen] = useState(false);
  // Lot L : suit la langue active (défaut builder EN pour les bilingues,
  // fallback FR si le builder n'a pas d'EN).
  const isEn = lang === "en";
  const subject =
    (isEn ? def.subject_en : def.subject_fr) || def.subject_fr || "";
  const body = (isEn ? def.body_en : def.body_fr) || def.body_fr || "";
  if (!subject && !body) return null;
  return (
    <div
      data-testid="direct-msg-default-ref"
      style={{
        marginTop: 14,
        border: `1px solid ${DS.border}`,
        borderRadius: DS.radiusControl,
        background: DS.pageBg,
        overflow: "hidden",
      }}
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        style={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "9px 12px",
          border: "none",
          background: "transparent",
          cursor: "pointer",
          fontFamily: DS.fontBody,
          fontSize: 12,
          fontWeight: 600,
          color: DS.textSecondary,
          textAlign: "left",
        }}
      >
        <Eye size={14} strokeWidth={2} color={DS.marine} />
        Texte par défaut actuel (référence{isEn ? " EN" : ""})
        <ChevronDown
          size={14}
          strokeWidth={2}
          style={{
            marginLeft: "auto",
            transform: open ? "rotate(180deg)" : "none",
            transition: "transform .15s",
          }}
        />
      </button>
      {open && (
        <div style={{ padding: "0 12px 12px" }}>
          <div
            style={{
              fontSize: 11,
              color: DS.textMuted,
              marginBottom: 8,
              lineHeight: 1.5,
            }}
          >
            Ce qui part au voyageur aujourd'hui (exemples : « Camille », lien
            d'exemple…). Recomposez votre version avec les vraies variables —
            ne collez pas les exemples.
          </div>
          <SubLabel>Objet</SubLabel>
          <div
            style={{
              fontSize: 12.5,
              color: DS.textSecondary,
              background: DS.card,
              border: `1px solid ${DS.border}`,
              borderRadius: 8,
              padding: "8px 11px",
              marginBottom: 10,
            }}
          >
            {subject}
          </div>
          <SubLabel>Corps</SubLabel>
          <div
            style={{
              fontSize: 12.5,
              color: DS.textSecondary,
              background: DS.card,
              border: `1px solid ${DS.border}`,
              borderRadius: 8,
              padding: "10px 12px",
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              lineHeight: 1.6,
            }}
          >
            {body}
          </div>
        </div>
      )}
    </div>
  );
});

// Badge « Créé par toi » (message manuel 3B).
function ManuelBadge() {
  return (
    <span
      data-testid="direct-msg-manuel-badge"
      style={{
        fontSize: 9,
        fontWeight: 700,
        letterSpacing: 0.3,
        padding: "3px 7px",
        borderRadius: 5,
        textTransform: "uppercase",
        background: DS.warningBg,
        color: DS.warningText,
        whiteSpace: "nowrap",
        flexShrink: 0,
      }}
    >
      Créé par vous
    </span>
  );
}

// Badge « Modifié » (message dont le contenu base est personnalisé, is_edited).
function EditedBadge() {
  return (
    <span
      data-testid="direct-msg-edited-badge"
      style={{
        fontSize: 9,
        fontWeight: 700,
        letterSpacing: 0.3,
        padding: "3px 7px",
        borderRadius: 5,
        textTransform: "uppercase",
        background: DS.cielSoft,
        color: DS.marineDeep,
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
      }}
    >
      <History size={11} strokeWidth={2} /> Modifié
    </span>
  );
}

function FieldLabel({ icon: Icon, label, hint }) {
  return (
    <div
      style={{
        fontSize: 12,
        fontWeight: 700,
        color: DS.textSecondary,
        marginBottom: 7,
        display: "flex",
        alignItems: "center",
        gap: 7,
      }}
    >
      <Icon size={15} strokeWidth={2} color={DS.marine} /> {label}
      {hint && (
        <span style={{ fontWeight: 400, color: DS.textMuted, fontSize: 11.5 }}>
          {hint}
        </span>
      )}
    </div>
  );
}

function SubLabel({ children }) {
  return (
    <div
      style={{
        fontSize: 11,
        fontWeight: 700,
        color: DS.textMuted,
        margin: "0 0 6px",
      }}
    >
      {children}
    </div>
  );
}

// ── Bloc « Condition d'envoi » ────────────────────────────────────────────────
// PERF : mémoïsé — le bloc « Condition d'envoi » (déclencheur, heure, offset,
// seuil d'avis) ne dépend PAS du texte en cours de saisie. `onSettings` est un
// relais d'identité stable côté vue (cf. `stableActionsRef`).
const TriggerBlock = memo(function TriggerBlock({
  m,
  isSys,
  onSettings,
  settingsBusy,
  engineConds,
}) {
  // LOT L1 : pour les slugs TEMPORELS, l'affichage lit les conditions RÉELLES
  // du moteur (endpoint direct_conditions_get — constantes du sweep). Les
  // descriptifs statiques TRIGGER_META ne restent que : (a) pour les sys
  // (événements transactionnels, exacts par nature) et (b) en secours si
  // l'endpoint est illisible (best-effort).
  const engine =
    engineConds && engineConds.temporels
      ? engineConds.temporels[m.slug] || null
      : null;
  const staticMeta = TRIGGER_META[m.slug] || { quand: "—", cond: null };
  const meta = engine
    ? { quand: engine.quand, cond: (engine.conditions || []).join(" · ") }
    : staticMeta;
  const engineDryRun = Boolean(engine && engineConds.dry_run_temporels);
  const isMoney = MONEY_SLUGS.has(m.slug);
  const isVoy = recipientRole(m) === "VOY";
  // Lot H : heure stockée (trigger_condition.hour) ou défaut maquette.
  const storedHour =
    m.trigger_condition && typeof m.trigger_condition === "object"
      ? m.trigger_condition.hour
      : undefined;
  const displayHour = storedHour || DEFAULT_HOURS[m.slug] || "09:00";

  return (
    <div
      data-testid="direct-msg-trigger"
      style={{
        border: `1px solid ${DS.border}`,
        borderRadius: DS.radiusControl,
        overflow: "hidden",
        background: DS.card,
      }}
    >
      {/* Ruban CHEMIN PAIEMENT — UNIQUEMENT sur les 4 messages argent */}
      {isMoney && (
        <div
          data-testid="direct-msg-money-ribbon"
          style={{
            background: DS.marineHero,
            color: "#fff",
            fontSize: 10,
            fontWeight: 600,
            letterSpacing: 0.4,
            padding: "6px 13px",
            display: "flex",
            alignItems: "center",
            gap: 7,
          }}
        >
          <Lock size={13} strokeWidth={2} /> LIÉ AU PAIEMENT — le moment d'envoi
          est fixé par l'application
        </div>
      )}

      {/* Ligne QUAND — libellé court métier (jamais le label technique DB) */}
      <TriggerRow
        label="Quand"
        value={
          <>
            {isSys ? (
              <Zap size={15} strokeWidth={2} color={DS.marine} />
            ) : (
              <CalendarClock size={15} strokeWidth={2} color={DS.marine} />
            )}
            <span>{meta.quand}</span>
            {engine && (
              <span
                data-testid="direct-engine-cond-badge"
                title={`Réglage réellement appliqué à l'envoi. ${engineConds.cron_label || ""}`}
                style={{
                  fontSize: 9.5,
                  fontWeight: 700,
                  letterSpacing: 0.4,
                  color: DS.successText,
                  background: DS.successBg,
                  borderRadius: 4,
                  padding: "2px 6px",
                  textTransform: "uppercase",
                  flexShrink: 0,
                }}
              >
                Réglage réel
              </span>
            )}
          </>
        }
        control={
          isSys ? (
            <LockChip label="Système" />
          ) : engine && engine.offset_editable ? (
            // LOT L3 (arbitrage Dimitri : départ seul, clamp 0-7) : le délai
            // devient RÉGLABLE — <select> 0..7 jours, save via settings
            // (clamp serveur miroir). Les autres temporels gardent leur
            // gradation figée (bouton inerte, lot dédié si besoin réel).
            <select
              data-testid="direct-offset-select"
              value={
                m.trigger_condition &&
                Number.isInteger(m.trigger_condition.offset_days)
                  ? m.trigger_condition.offset_days
                  : (engine.offset_days ?? 0)
              }
              disabled={settingsBusy}
              onChange={(e) =>
                onSettings({ offset_days: Number(e.target.value) })
              }
              style={{
                border: `1px solid ${DS.border}`,
                borderRadius: 6,
                background: DS.card,
                color: DS.marine,
                fontWeight: 600,
                fontSize: 12,
                fontFamily: DS.fontBody,
                padding: "3px 6px",
                cursor: settingsBusy ? "wait" : "pointer",
              }}
            >
              {[0, 1, 2, 3, 4, 5, 6, 7].map((d) => (
                <option key={d} value={d}>
                  {d === 0
                    ? "Le jour du départ"
                    : d === 1
                      ? "1 jour après"
                      : `${d} jours après`}
                </option>
              ))}
            </select>
          ) : (
            // 0.1-B « honnêteté conditions » (décision Dimitri B2) : la fenêtre
            // n'est PAS réglable — on dit POURQUOI (tooltip) au lieu d'un
            // bouton inerte « Modifier / bientôt » qui promettait un réglage.
            <LockChip
              label="Système"
              title={
                engine
                  ? "Fenêtre réglée par l'application : la gradation J-2 / J-1 / jour J est un triple filet pour la caution — décaler ou retirer une marche, c'est risquer un voyageur sans caution le jour J."
                  : "Fenêtre réglée par l'application (passage quotidien)."
              }
            />
          )
        }
      />
      {/* LOT L3 — subtilité honnête (demande Dimitri) : changer le délai en
          cours de route peut créer un trou/chevauchement d'UN jour. */}
      {engine && engine.offset_editable && (
        <div
          data-testid="direct-offset-gap-note"
          style={{
            padding: "0 13px 8px 13px",
            fontSize: 11,
            color: DS.textMuted,
            lineHeight: 1.45,
          }}
        >
          Changer ce délai s'applique dès le prochain passage quotidien — un
          départ déjà passé peut ne pas retomber dans la nouvelle fenêtre
          (décalage d'un jour possible au moment du changement, jamais de double
          envoi).
        </div>
      )}
      {/* LOT L2 — PAUSE EFFECTIVE (règle « texte d'aide = état actuel ») :
          depuis L2 le sweep honore la pastille Brouillon pour les 4 slugs
          temporels — la note n'apparaît QUE pour un slug réellement piloté
          (présent dans les conditions moteur) ET en brouillon. */}
      {engine && m.status === "brouillon" && (
        <div
          data-testid="direct-pause-effective-note"
          style={{
            padding: "7px 13px",
            fontSize: 11,
            fontWeight: 600,
            color: DS.textSecondary,
            background: DS.pageBg,
            borderBottom: `1px solid ${DS.border}`,
          }}
        >
          En PAUSE : cet envoi automatique est suspendu — il ne partira pas au
          passage quotidien. Repasse la pastille sur « Actif » pour le relancer.
        </div>
      )}
      {/* LOT L1 — état RÉEL du gate d'envoi des temporels (règle « texte
          d'aide = état actuel ») : tant que MESSAGES_DRY_RUN n'est pas levé,
          ces messages sont simulés (trace journal), rien ne part. */}
      {engineDryRun && (
        <div
          data-testid="direct-engine-dryrun-note"
          style={{
            padding: "7px 13px",
            fontSize: 11,
            fontWeight: 600,
            color: DS.warningText,
            background: DS.warningBg,
            borderBottom: `1px solid ${DS.border}`,
          }}
        >
          Essai sans envoi : rien ne part tant que les envois aux voyageurs ne
          sont pas ouverts.
        </div>
      )}

      {/* Système d'avis « option a » — SEUIL DE RESSENTI, réglable UNIQUEMENT
          sur le message de départ voyageur. Écrit trigger_condition.feedback_threshold
          (clamp serveur 1..5, miroir du décideur lib/feedback-handlers.js).
          DORMANT : tant que le système d'avis n'est pas activé par l'équipe
          (feu juridique + flag), ce réglage est enregistré mais sans effet —
          le texte d'aide le dit (règle « texte d'aide = état actuel »). */}
      {m.slug === "depart_voyageur" && (
        <>
          <TriggerRow
            label="Seuil avis"
            value={
              <>
                <Star size={15} strokeWidth={2} color={DS.marine} />
                <span>
                  Ressenti ≥ seuil → invitation Google · &lt; seuil → dialogue
                  interne
                </span>
              </>
            }
            control={
              <select
                data-testid="direct-feedback-threshold-select"
                value={
                  m.trigger_condition &&
                  Number.isInteger(m.trigger_condition.feedback_threshold)
                    ? m.trigger_condition.feedback_threshold
                    : 4 /* DEFAULT_FEEDBACK_THRESHOLD — miroir lib/feedback-handlers.js */
                }
                disabled={settingsBusy}
                onChange={(e) =>
                  onSettings({ feedback_threshold: Number(e.target.value) })
                }
                style={{
                  border: `1px solid ${DS.border}`,
                  borderRadius: 6,
                  background: DS.card,
                  color: DS.marine,
                  fontWeight: 600,
                  fontSize: 12,
                  fontFamily: DS.fontBody,
                  padding: "3px 6px",
                  cursor: settingsBusy ? "wait" : "pointer",
                }}
              >
                {[1, 2, 3, 4, 5].map((n) => (
                  <option key={n} value={n}>
                    {n} étoile{n > 1 ? "s" : ""} et plus
                  </option>
                ))}
              </select>
            }
          />
          <div
            data-testid="direct-feedback-threshold-note"
            style={{
              padding: "0 13px 10px 13px",
              fontSize: 11,
              color: DS.textMuted,
              lineHeight: 1.45,
            }}
          >
            Réglage enregistré — le système d'avis n'est pas encore actif :
            aucun ressenti n'est demandé aux voyageurs pour l'instant. Ce seuil
            s'appliquera à son activation par l'équipe.
          </div>
        </>
      )}

      {/* Ligne HEURE — sys : immédiat verrouillé ; temp (lot H) : timepick
          maquette, écrit trigger_condition.hour (DORMANT : le sweep quotidien
          n'est pas touché). */}
      <TriggerRow
        label="Heure"
        value={
          isSys ? (
            <span style={{ color: DS.textMuted, fontWeight: 400 }}>
              Immédiat — à l'événement
            </span>
          ) : (
            <label
              data-testid="direct-msg-hour-pick"
              style={{
                border: `1px solid ${DS.ciel}`,
                background: DS.cielSoft,
                color: DS.marine,
                borderRadius: 7,
                padding: "5px 11px",
                fontSize: 13,
                fontWeight: 600,
                display: "inline-flex",
                alignItems: "center",
                gap: 7,
                cursor: settingsBusy ? "wait" : "pointer",
              }}
            >
              <Clock size={14} strokeWidth={2} />
              <input
                type="time"
                data-testid="direct-msg-hour-input"
                value={displayHour}
                disabled={settingsBusy}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v) {
                    onSettings({ hour: v });
                  } else {
                    // ✕ natif de l'input time : re-snap immédiat sur la valeur
                    // affichée (rien n'est écrit, pas de champ vide transitoire).
                    e.target.value = displayHour;
                  }
                }}
                style={{
                  border: "none",
                  background: "transparent",
                  color: DS.marine,
                  fontWeight: 600,
                  fontSize: 13,
                  fontFamily: DS.fontBody,
                  outline: "none",
                  cursor: "inherit",
                }}
              />
            </label>
          )
        }
        control={isSys ? <LockChip /> : !storedHour ? <DefTag /> : null}
      />
      {/* Note honnête (règle « texte d'aide = état actuel ») : l'heure est
          stockée, l'envoi effectif reste au passage quotidien du système.
          TROIS cas distincts depuis PR #1118 — les 2 notifications internes
          sont câblées ET non gatées MESSAGES_DRY_RUN (convention des envois
          admin de lib/direct-journey-sweep.js) : leur dire « rien ne part tant
          que la bascule n'a pas eu lieu » serait faux, elles partent déjà. */}
      {!isSys && (
        <div
          data-testid="direct-msg-hour-note"
          style={{
            padding: "0 13px 10px 13px",
            fontSize: 11,
            color: DS.textMuted,
            lineHeight: 1.45,
          }}
        >
          {NOT_WIRED_SLUGS.has(m.slug)
            ? "Réglage enregistré — ce message n'est branché à aucun envoi automatique pour l'instant. Rien ne part."
            : recipientRole(m) === "ADM"
              ? "Réglage enregistré — cette notification interne part au passage quotidien du système : elle n'attend pas l'ouverture des envois voyageur."
              : "Réglage enregistré — l'envoi effectif reste au passage quotidien du système tant que les envois voyageur ne sont pas ouverts."}
          {/* Heure RÉELLE du passage qui envoie ce message (règle « le texte
              d'aide décrit l'état actuel ») : l'heure affichée au-dessus est
              celle-là, pas une intention. Cf. TEMPORAL_PASSAGE. */}
          {PASSAGE_NOTE[TEMPORAL_PASSAGE[m.slug]] && (
            <div data-testid="direct-msg-passage-note" style={{ marginTop: 4 }}>
              {PASSAGE_NOTE[TEMPORAL_PASSAGE[m.slug]]}
            </div>
          )}
        </div>
      )}

      {/* Ligne SEULEMENT SI — affichée dès qu'une CONDITION existe (admin
          inclus — maquette, décision Dimitri lot SUIVI ADMIN) ; la ligne
          « Aucune condition » reste voyageur-only (inutile pour les notifs
          admin sans condition). */}
      {(isVoy || meta.cond) &&
        (meta.cond ? (
          <TriggerRow
            label="Seulement si"
            value={
              <>
                <Filter size={15} strokeWidth={2} color={DS.warning} />
                <span>{meta.cond}</span>
              </>
            }
            control={
              // 0.1-B « honnêteté conditions » : les conditions découlent des
              // statuts réels de la réservation — pas un réglage à venir.
              <LockChip
                label="Système"
                title="Condition réglée par l'application : elle suit l'étape où en est la réservation ; la changer à la main décalerait les messages par rapport à la réservation."
              />
            }
          />
        ) : (
          <TriggerRow
            label="Seulement si"
            value={
              <span style={{ color: DS.textMuted, fontWeight: 400 }}>
                Aucune condition — le message part toujours
              </span>
            }
            control={
              // 0.1-B : rien à « ajouter » — un transactionnel part à
              // l'événement du parcours, sans condition réglable.
              <LockChip
                label="Système"
                title="Réglé par l'application : ce message part automatiquement à son étape du parcours, sans condition à choisir."
              />
            }
          />
        ))}
    </div>
  );
});

// Chip « défaut établi » (reprend l'existant, non modifié par défaut).
function DefTag() {
  return (
    <span
      style={{
        fontSize: 9,
        fontWeight: 600,
        letterSpacing: 0.3,
        background: DS.pageBg,
        color: DS.textSecondary,
        padding: "3px 7px",
        borderRadius: 4,
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
      }}
    >
      <History size={11} strokeWidth={2} /> heure par défaut
    </span>
  );
}

function TriggerRow({ label, value, control }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        padding: "11px 13px",
        borderTop: `1px dashed ${DS.border}`,
      }}
    >
      <span
        style={{
          fontSize: 10.5,
          fontWeight: 700,
          letterSpacing: 0.6,
          textTransform: "uppercase",
          color: DS.textMuted,
          width: 64,
          flexShrink: 0,
        }}
      >
        {label}
      </span>
      <span
        style={{
          flex: 1,
          fontSize: 13,
          color: DS.marineDark,
          fontWeight: 500,
          display: "flex",
          alignItems: "center",
          gap: 8,
          flexWrap: "wrap",
          minWidth: 0,
        }}
      >
        {value}
      </span>
      <span style={{ flexShrink: 0 }}>{control}</span>
    </div>
  );
}

function LockChip({ label, title }) {
  return (
    <span
      title={title}
      style={{
        fontSize: 11,
        color: DS.textMuted,
        display: "inline-flex",
        alignItems: "center",
        gap: 5,
      }}
    >
      <Lock size={13} strokeWidth={2} />
      {label}
    </span>
  );
}

// (SoonMiniButton retiré — 0.1-B : les 3 boutons inertes « Modifier /
// Ajouter » des lignes de conditions figées sont remplacés par un LockChip
// « Système » + tooltip expliquant POURQUOI c'est figé.)

// ── Canaux ────────────────────────────────────────────────────────────────────
// Carte canal (lot C : interactive). `locked` = toggle inerte + cadenas (email
// sur les sys).
function ChannelBox({
  active,
  locked,
  lockedTitle,
  label,
  icon: Icon,
  onToggle,
  disabled,
  testid,
}) {
  return (
    <div
      data-testid={testid}
      title={locked ? lockedTitle : undefined}
      style={{
        flex: 1,
        border: `1px solid ${active ? DS.success : DS.border}`,
        background: active ? DS.successBg : DS.card,
        borderRadius: DS.radiusControl,
        padding: "11px 13px",
        display: "flex",
        alignItems: "center",
        gap: 10,
      }}
    >
      <Icon
        size={16}
        strokeWidth={2}
        color={active ? DS.successText : DS.marine}
      />
      <span
        style={{ fontWeight: 600, fontSize: 13, flex: 1, color: DS.marineDark }}
      >
        {label}
      </span>
      {locked && (
        <Lock
          data-testid={testid ? `${testid}-lock` : undefined}
          size={13}
          strokeWidth={2}
          color={DS.textMuted}
        />
      )}
      <Toggle
        on={active}
        disabled={disabled || locked}
        onClick={locked ? undefined : onToggle}
        testid={testid ? `${testid}-toggle` : undefined}
      />
    </div>
  );
}

// Interrupteur. Sans `onClick` : purement visuel (compat #922). Avec `onClick` :
// bouton accessible (lot C).
function Toggle({ on, onClick, disabled, testid }) {
  const knob = (
    <span
      style={{
        position: "absolute",
        width: 16,
        height: 16,
        borderRadius: "50%",
        background: "#fff",
        top: 2,
        left: on ? 16 : 2,
        transition: "left .15s",
      }}
    />
  );
  const baseStyle = {
    width: 34,
    height: 20,
    borderRadius: 11,
    background: on ? DS.success : DS.borderHover,
    position: "relative",
    flexShrink: 0,
    display: "inline-block",
  };
  if (!onClick) {
    return (
      <span data-testid={testid} style={baseStyle}>
        {knob}
      </span>
    );
  }
  return (
    <button
      type="button"
      data-testid={testid}
      role="switch"
      aria-checked={on}
      disabled={disabled}
      onClick={onClick}
      style={{
        ...baseStyle,
        border: "none",
        padding: 0,
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.6 : 1,
      }}
    >
      {knob}
    </button>
  );
}

// ── Bascule langue FR/EN (lot L — bilingues uniquement) ───────────────────────
function LangToggle({ lang, onChange }) {
  return (
    // B4-ext — FR / EN / ES (miroir de l'éditeur Plateformes). Les 3 langues
    // sont ENREGISTRÉES ; voir EditorCard pour la note sur le service de l'ES
    // à l'envoi (encore traduit à la volée depuis le FR).
    <div style={{ display: "inline-flex", gap: 4 }}>
      {["fr", "en", "es"].map((l) => {
        const on = lang === l;
        return (
          <button
            key={l}
            type="button"
            data-testid={`direct-msg-lang-${l}`}
            onClick={() => onChange(l)}
            style={{
              border: `0.5px solid ${on ? DS.marine : DS.border}`,
              borderRadius: 20,
              padding: "4px 11px",
              fontSize: 11,
              fontWeight: 700,
              cursor: "pointer",
              background: on ? DS.marine : DS.card,
              color: on ? "#fff" : DS.textSecondary,
              fontFamily: DS.fontBody,
            }}
          >
            {l.toUpperCase()}
          </button>
        );
      })}
    </div>
  );
}

// (InsertMenu supprimé — M12 : le picker « + Info voyageur » vit désormais DANS
// le SegmentEditor partagé, par champ, comme sur l'onglet Plateformes.)

// ── Bandeau ESPAGNOL : « enregistré, pas encore envoyé » ─────────────────────
//
// ⚠️ L'APERÇU DIT LA VÉRITÉ, MÊME QUAND ELLE EST INCONFORTABLE (QA #1095).
// B4-ext PERSISTE `subject_es` / `body_es`, et B5 promet « tu vois exactement ce
// que le voyageur recevra ». Sur l'onglet ES, c'est FAUX aujourd'hui : la façade
// d'envoi (lib/journey-content-source.js, DB_CONTENT_COLUMNS) ne lit que le
// FR/EN, et `resolveJourneyTemplate` (lib/journey-templates.js) n'a PAS de
// branche `es` pour `opts.dbRow` → l'espagnol enregistré ici n'est jamais servi,
// et le voyageur hispanophone reçoit la version française (traduite à la volée
// par lib/email-i18n-sender.js). Le câblage du chemin d'envoi est le lot B6
// (#1099) — hors périmètre B5. En attendant, on le DIT, en clair, au-dessus de
// l'aperçu. Ce bandeau disparaît avec B6 (règle « texte d'aide = état réel »).
function EsNotSentYetBanner() {
  return (
    <div
      data-testid="direct-preview-es-not-sent"
      style={{
        flex: "0 0 auto",
        display: "flex",
        alignItems: "flex-start",
        gap: 9,
        border: `1px solid ${DS.warning}`,
        background: DS.warningBg,
        borderRadius: DS.radiusControl,
        padding: "10px 12px",
        marginBottom: 12,
        color: DS.warningText,
        fontSize: 12,
        lineHeight: 1.5,
      }}
    >
      <AlertTriangle
        size={15}
        strokeWidth={2.2}
        style={{ flex: "0 0 auto", marginTop: 1 }}
      />
      <div>
        <strong>Contenu espagnol enregistré, pas encore envoyé.</strong>{" "}
        Aujourd'hui, le voyageur hispanophone reçoit la version française
        (l'envoi ne sait encore utiliser que le français et l'anglais). Cet aperçu
        montre le texte que vous enregistrez, pas celui qui part.
      </div>
    </div>
  );
}

// ── Colonne droite : aperçu device ────────────────────────────────────────────
// Reflète le BROUILLON en cours, avec les {tokens} remplacés par leurs valeurs
// d'exemple (aperçu « ce que reçoit le voyageur »). Le contenu stocké garde
// toujours les {tokens}.
//
// PERF — `memo` (plus bas) + les deux `useMemo` ci-dessous : taper dans le CORPS
// ne doit pas relancer le rendu de gabarit de l'OBJET (et réciproquement), et
// une frappe dans la recherche du rail ne doit plus re-rendre l'aperçu du tout.
// L'aperçu reste STRICTEMENT à jour : `rawSubject`/`rawBody`/`previewVars` sont
// les seules entrées possibles du rendu, elles sont toutes dans les
// dépendances — un changement de l'une recalcule immédiatement.
function PreviewCardBase({
  subject: rawSubject,
  body: rawBody,
  previewVars,
  sticky,
}) {
  // B5 : rendu 2 passes IDENTIQUE au serveur — évaluation des blocs {#if …} avec
  // le contexte simulé (drapeaux du scénario), PUIS interpolation des {tokens}.
  // Appliqué à l'OBJET comme au CORPS (le serveur applique renderBody aux deux).
  const subject = useMemo(
    () => renderDirectPreview(rawSubject, previewVars),
    [rawSubject, previewVars],
  );
  const body = useMemo(
    () => renderDirectPreview(rawBody, previewVars),
    [rawBody, previewVars],
  );
  return (
    <div
      data-testid="direct-msg-preview"
      style={{
        borderRadius: 20,
        overflow: "hidden",
        boxShadow:
          "0 24px 50px -18px rgba(20,51,80,.42), 0 4px 12px -6px rgba(20,51,80,.2)",
        border: `1px solid rgba(33,83,135,.12)`,
        position: sticky ? "sticky" : "static",
        top: 16,
      }}
    >
      <div
        style={{
          background: `linear-gradient(160deg, ${DS.marine} 0%, ${DS.marine} 55%, ${DS.marineDark} 100%)`,
          color: "#fff",
          padding: "24px 22px 20px",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            width: 40,
            height: 40,
            borderRadius: 11,
            background: DS.ciel,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: DS.marineDeep,
            marginBottom: 14,
            boxShadow: "0 6px 16px -6px rgba(0,0,0,.35)",
          }}
        >
          <Flower2 size={22} strokeWidth={2} />
        </div>
        <div
          style={{
            fontSize: 11,
            letterSpacing: 1,
            color: "#a9cbec",
            textTransform: "uppercase",
            display: "flex",
            alignItems: "center",
            gap: 6,
          }}
        >
          <Mail size={13} strokeWidth={2} /> Objet
        </div>
        <div
          style={{
            fontFamily: DS.fontDisplay,
            fontWeight: 600,
            fontSize: 19,
            letterSpacing: -0.2,
            marginTop: 7,
            lineHeight: 1.25,
          }}
        >
          {subject || "(objet)"}
        </div>
      </div>
      <div
        style={{
          background: "#fff",
          padding: "20px 22px",
          fontSize: 13,
          color: "#33414f",
          lineHeight: 1.68,
        }}
      >
        {toParagraphs(body).map((line, i) =>
          line.trim() === "" ? (
            <div key={i} style={{ height: 10 }} />
          ) : (
            <p key={i} style={{ margin: "0 0 10px" }}>
              {line}
            </p>
          ),
        )}
      </div>
    </div>
  );
}

// PERF : l'aperçu ne se re-rend QUE si l'une de ses 4 entrées change (objet,
// corps, contexte d'aperçu, `sticky`). Comparaison par défaut de React.memo
// (identité des props) — `previewVars` est déjà mémoïsé côté vue.
const PreviewCard = memo(PreviewCardBase);
